import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, Phone, Mail, Car, Calendar, Clock, 
  ChevronRight, CheckCircle2, Sparkles, Shield, 
  Droplets, Sun, ArrowRight, ChevronLeft, MapPin,
  Building2, Truck, ChevronDown, Palette, Loader2,
  CarFront, Info, Zap, AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://supremedetailstudio-production.up.railway.app';

const fmt = (n) => Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

/* ---------------- DATA ---------------- */

// Default service locations - will be updated from API
const DEFAULT_SERVICE_LOCATIONS = [
  { 
    id: 'shop', 
    label: 'In Shop', 
    description: 'Drop off at our shop location',
    icon: Building2,
    upcharge: 0
  },
  { 
    id: 'mobile', 
    label: 'Mobile Service', 
    description: 'We come to you',
    icon: Truck,
    upcharge: 50
  },
];

const VEHICLE_TYPES = [
  { id: 'sedan', label: 'Sedan / Coupe', upcharge: 0, icon: '🚗', category: 'sedan' },
  { id: 'suv-2row', label: '2-Row SUV / Crossover / Truck', upcharge: 50, icon: '🚙', category: 'suv' },
  { id: 'suv-3row', label: '3-Row SUV / Large Truck', upcharge: 100, icon: '🚐', category: 'large' },
];

// Paint colors database by manufacturer - curated common colors
const PAINT_COLORS_BY_MAKE = {
  'Toyota': [
    { code: '040', name: 'Super White', hex: '#FFFFFF' },
    { code: '1F7', name: 'Classic Silver Metallic', hex: '#C0C0C0' },
    { code: '1G3', name: 'Magnetic Gray Metallic', hex: '#6B6B6B' },
    { code: '202', name: 'Black', hex: '#0A0A0A' },
    { code: '3T3', name: 'Ruby Flare Pearl', hex: '#8B2942' },
    { code: '8X8', name: 'Blueprint', hex: '#1E3A5F' },
    { code: '1J9', name: 'Celestial Silver Metallic', hex: '#A8A8A8' },
    { code: '8W7', name: 'Blue Crush Metallic', hex: '#0066B3' },
    { code: '3U5', name: 'Supersonic Red', hex: '#CC0000' },
    { code: '089', name: 'Wind Chill Pearl', hex: '#E8E8E8' },
  ],
  'Honda': [
    { code: 'NH-883P', name: 'Platinum White Pearl', hex: '#E8E8E8' },
    { code: 'NH-731P', name: 'Crystal Black Pearl', hex: '#1A1A1A' },
    { code: 'NH-830M', name: 'Lunar Silver Metallic', hex: '#B8B8B8' },
    { code: 'R-569M', name: 'Radiant Red Metallic', hex: '#CC2222' },
    { code: 'B-588P', name: 'Obsidian Blue Pearl', hex: '#1E3050' },
    { code: 'NH-797M', name: 'Modern Steel Metallic', hex: '#5A5A5A' },
    { code: 'YR-592M', name: 'Sonic Gray Pearl', hex: '#7A7A7A' },
    { code: 'G-548M', name: 'Aegean Blue Metallic', hex: '#2E5A7A' },
    { code: 'NH-788P', name: 'San Marino Red', hex: '#AA2233' },
    { code: 'YR-604M', name: 'Meteorite Gray Metallic', hex: '#4A4A4A' },
  ],
  'Ford': [
    { code: 'YZ', name: 'Oxford White', hex: '#FFFFFF' },
    { code: 'UM', name: 'Shadow Black', hex: '#0A0A0A' },
    { code: 'JS', name: 'Ingot Silver Metallic', hex: '#A0A0A0' },
    { code: 'J7', name: 'Magnetic Metallic', hex: '#4A4A4A' },
    { code: 'D1', name: 'Race Red', hex: '#CC0000' },
    { code: 'FT', name: 'Blue Metallic', hex: '#1E3A5F' },
    { code: 'N1', name: 'Agate Black Metallic', hex: '#1A1A1A' },
    { code: 'HG', name: 'Velocity Blue', hex: '#0044AA' },
    { code: 'AJ', name: 'Rapid Red Metallic', hex: '#AA2233' },
    { code: 'LK', name: 'Carbonized Gray Metallic', hex: '#3A3A3A' },
  ],
  'Chevrolet': [
    { code: 'GAZ', name: 'Summit White', hex: '#FFFFFF' },
    { code: 'GBA', name: 'Black', hex: '#0A0A0A' },
    { code: 'GAN', name: 'Silver Ice Metallic', hex: '#C0C0C0' },
    { code: 'G9K', name: 'Shadow Gray Metallic', hex: '#5A5A5A' },
    { code: 'G7C', name: 'Red Hot', hex: '#CC0000' },
    { code: 'GXH', name: 'Northsky Blue Metallic', hex: '#1E4070' },
    { code: 'GNO', name: 'Mosaic Black Metallic', hex: '#1A1A1A' },
    { code: 'G1W', name: 'Iridescent Pearl Tricoat', hex: '#E8E8E8' },
    { code: 'GKZ', name: 'Cherry Red Tintcoat', hex: '#8B0000' },
    { code: 'GJI', name: 'Satin Steel Metallic', hex: '#7A7A7A' },
  ],
  'BMW': [
    { code: '300', name: 'Alpine White', hex: '#FFFFFF' },
    { code: '668', name: 'Jet Black', hex: '#0A0A0A' },
    { code: 'A52', name: 'Space Gray Metallic', hex: '#5A5A5A' },
    { code: 'A89', name: 'Imperial Blue Metallic', hex: '#1E3050' },
    { code: 'B39', name: 'Mineral Gray Metallic', hex: '#6B6B6B' },
    { code: 'C1G', name: 'Phytonic Blue Metallic', hex: '#2E4A6A' },
    { code: '475', name: 'Black Sapphire Metallic', hex: '#1A1A2E' },
    { code: 'C31', name: 'Portimao Blue Metallic', hex: '#0044AA' },
    { code: 'X13', name: 'Brooklyn Gray Metallic', hex: '#4A4A4A' },
    { code: 'C4A', name: 'Skyscraper Gray Metallic', hex: '#8A8A8A' },
  ],
  'Mercedes-Benz': [
    { code: '149', name: 'Polar White', hex: '#FFFFFF' },
    { code: '040', name: 'Black', hex: '#0A0A0A' },
    { code: '992', name: 'Selenite Gray Metallic', hex: '#6A6A6A' },
    { code: '890', name: 'Obsidian Black Metallic', hex: '#1A1A1A' },
    { code: '197', name: 'Obsidian Black', hex: '#0A0A0A' },
    { code: '359', name: 'Brilliant Blue Metallic', hex: '#0044AA' },
    { code: '896', name: 'Designo Diamond White', hex: '#F0F0F0' },
    { code: '988', name: 'Diamond Silver Metallic', hex: '#C0C0C0' },
    { code: '589', name: 'Lunar Blue Metallic', hex: '#1E3A5F' },
    { code: '996', name: 'High-Tech Silver Metallic', hex: '#A8A8A8' },
  ],
  'Audi': [
    { code: 'LY9C', name: 'Ibis White', hex: '#FFFFFF' },
    { code: 'LY9T', name: 'Mythos Black Metallic', hex: '#0A0A0A' },
    { code: 'LX7R', name: 'Daytona Gray Pearl', hex: '#5A5A5A' },
    { code: 'LZ9Y', name: 'Glacier White Metallic', hex: '#E8E8E8' },
    { code: 'LY5Q', name: 'Navarra Blue Metallic', hex: '#1E3050' },
    { code: 'LX6X', name: 'Floret Silver Metallic', hex: '#B8B8B8' },
    { code: 'LY9Z', name: 'Brilliant Black', hex: '#0A0A0A' },
    { code: 'LZ5F', name: 'Ascari Blue Metallic', hex: '#2E4A7A' },
    { code: 'LY8X', name: 'Quantum Gray', hex: '#4A4A4A' },
    { code: 'LZ3M', name: 'Tango Red Metallic', hex: '#CC2222' },
  ],
  'Nissan': [
    { code: 'QAB', name: 'Brilliant Silver Metallic', hex: '#C0C0C0' },
    { code: 'KH3', name: 'Super Black', hex: '#0A0A0A' },
    { code: 'QM1', name: 'Fresh Powder', hex: '#FFFFFF' },
    { code: 'RAY', name: 'Gun Metallic', hex: '#5A5A5A' },
    { code: 'RBY', name: 'Deep Blue Pearl', hex: '#1E3050' },
    { code: 'NAH', name: 'Scarlet Ember Tintcoat', hex: '#8B2942' },
    { code: 'KAD', name: 'Magnetic Black Pearl', hex: '#1A1A1A' },
    { code: 'NAJ', name: 'Boulder Gray Pearl', hex: '#6B6B6B' },
    { code: 'EAH', name: 'Pearl White Tricoat', hex: '#F0F0F0' },
    { code: 'GAT', name: 'Storm Blue', hex: '#2E4A6A' },
  ],
  'Hyundai': [
    { code: 'NKA', name: 'Aurora Black Pearl', hex: '#0A0A0A' },
    { code: 'W8Y', name: 'Quartz White Pearl', hex: '#FFFFFF' },
    { code: 'R4R', name: 'Calypso Red', hex: '#CC2222' },
    { code: 'YB7', name: 'Cyber Gray Metallic', hex: '#6B6B6B' },
    { code: 'N4U', name: 'Phantom Black', hex: '#1A1A1A' },
    { code: 'S3B', name: 'Shimmering Silver', hex: '#C0C0C0' },
    { code: 'WC9', name: 'Atlas White', hex: '#FFFFFF' },
    { code: 'N5M', name: 'Amazon Gray', hex: '#5A5A5A' },
    { code: 'PH3', name: 'Portofino Gray', hex: '#8A8A8A' },
    { code: 'W4Y', name: 'Creamy White Pearl', hex: '#F0E8E0' },
  ],
  'Kia': [
    { code: 'ABP', name: 'Aurora Black Pearl', hex: '#0A0A0A' },
    { code: 'SWP', name: 'Snow White Pearl', hex: '#FFFFFF' },
    { code: 'C4G', name: 'Gravity Gray', hex: '#5A5A5A' },
    { code: 'KLG', name: 'Steel Gray', hex: '#6B6B6B' },
    { code: 'B4U', name: 'Deep Sea Blue', hex: '#1E3050' },
    { code: 'R4R', name: 'Runway Red', hex: '#CC2222' },
    { code: 'MST', name: 'Mineral Silver', hex: '#C0C0C0' },
    { code: 'ACB', name: 'Clear White', hex: '#FFFFFF' },
    { code: 'D9Y', name: 'Ceramic Silver', hex: '#B8B8B8' },
    { code: 'P2M', name: 'Platinum Graphite', hex: '#4A4A4A' },
  ],
  'Mazda': [
    { code: '25D', name: 'Snowflake White Pearl', hex: '#FFFFFF' },
    { code: '41W', name: 'Jet Black Mica', hex: '#0A0A0A' },
    { code: '46V', name: 'Soul Red Crystal Metallic', hex: '#8B2942' },
    { code: '47C', name: 'Machine Gray Metallic', hex: '#5A5A5A' },
    { code: '42M', name: 'Deep Crystal Blue Mica', hex: '#1E3050' },
    { code: '45P', name: 'Sonic Silver Metallic', hex: '#C0C0C0' },
    { code: '46G', name: 'Polymetal Gray Metallic', hex: '#6B6B6B' },
    { code: '41V', name: 'Titanium Flash Mica', hex: '#4A4A4A' },
    { code: '25Y', name: 'Rhodium White Premium', hex: '#E8E8E8' },
    { code: '42S', name: 'Eternal Blue Mica', hex: '#2E4A6A' },
  ],
  'Subaru': [
    { code: 'K1X', name: 'Crystal White Pearl', hex: '#FFFFFF' },
    { code: 'D4S', name: 'Crystal Black Silica', hex: '#0A0A0A' },
    { code: 'M1Y', name: 'Ice Silver Metallic', hex: '#C0C0C0' },
    { code: 'MAD', name: 'Magnetite Gray Metallic', hex: '#5A5A5A' },
    { code: 'H8R', name: 'Pure Red', hex: '#CC2222' },
    { code: 'E8H', name: 'Dark Blue Pearl', hex: '#1E3050' },
    { code: 'N3U', name: 'Abyss Blue Pearl', hex: '#1A2A4A' },
    { code: '4S2', name: 'WR Blue Pearl', hex: '#0044AA' },
    { code: 'GT2', name: 'Autumn Green Metallic', hex: '#2A4A2A' },
    { code: 'G2U', name: 'Sapphire Blue Pearl', hex: '#2E4A7A' },
  ],
  'Volkswagen': [
    { code: 'LC9A', name: 'Pure White', hex: '#FFFFFF' },
    { code: 'LC9X', name: 'Deep Black Pearl', hex: '#0A0A0A' },
    { code: 'LB7W', name: 'Platinum Gray Metallic', hex: '#6B6B6B' },
    { code: 'LA7W', name: 'Reflex Silver Metallic', hex: '#C0C0C0' },
    { code: 'LH1Z', name: 'Atlantic Blue Metallic', hex: '#1E3050' },
    { code: 'LC9Z', name: 'Urano Gray', hex: '#5A5A5A' },
    { code: 'LG7S', name: 'Pyrite Silver Metallic', hex: '#8A8A8A' },
    { code: 'LP5Z', name: 'Cornflower Blue', hex: '#4A6A9A' },
    { code: 'LY3D', name: 'Tornado Red', hex: '#CC2222' },
    { code: 'LS8Y', name: 'Rising Blue Metallic', hex: '#2E5A7A' },
  ],
  'Lexus': [
    { code: '077', name: 'Ultra White', hex: '#FFFFFF' },
    { code: '212', name: 'Obsidian', hex: '#0A0A0A' },
    { code: '1J7', name: 'Atomic Silver', hex: '#A8A8A8' },
    { code: '1L1', name: 'Eminent White Pearl', hex: '#F0F0F0' },
    { code: '8X5', name: 'Ultrasonic Blue Mica 2.0', hex: '#0044AA' },
    { code: '3T5', name: 'Matador Red Mica', hex: '#8B2942' },
    { code: '1G1', name: 'Nebula Gray Pearl', hex: '#6B6B6B' },
    { code: '223', name: 'Caviar', hex: '#1A1A1A' },
    { code: '8Y5', name: 'Grecian Water', hex: '#4A7A8A' },
    { code: '1K4', name: 'Cloudburst Gray', hex: '#5A5A5A' },
  ],
  'Tesla': [
    { code: 'PPSW', name: 'Pearl White Multi-Coat', hex: '#FFFFFF' },
    { code: 'PBSB', name: 'Solid Black', hex: '#0A0A0A' },
    { code: 'PMNG', name: 'Midnight Silver Metallic', hex: '#5A5A5A' },
    { code: 'PPSB', name: 'Deep Blue Metallic', hex: '#1E3050' },
    { code: 'PPSR', name: 'Red Multi-Coat', hex: '#CC2222' },
    { code: 'MSS', name: 'Midnight Cherry Red', hex: '#4A1A2A' },
    { code: 'QW', name: 'Quicksilver', hex: '#C0C0C0' },
    { code: 'PMSS', name: 'Ultra White', hex: '#F8F8F8' },
  ],
  'Jeep': [
    { code: 'PW7', name: 'Bright White', hex: '#FFFFFF' },
    { code: 'PX8', name: 'Black', hex: '#0A0A0A' },
    { code: 'PAU', name: 'Billet Silver Metallic', hex: '#A8A8A8' },
    { code: 'PGG', name: 'Granite Crystal Metallic', hex: '#5A5A5A' },
    { code: 'PRM', name: 'Velvet Red Pearl', hex: '#6A2A3A' },
    { code: 'PBM', name: 'Patriot Blue Pearl', hex: '#1E3050' },
    { code: 'PDN', name: 'Sting Gray', hex: '#7A7A7A' },
    { code: 'PJD', name: 'Hydro Blue Pearl', hex: '#4A6A7A' },
    { code: 'PRY', name: 'Firecracker Red', hex: '#CC2222' },
    { code: 'PAE', name: 'Earl', hex: '#3A3A3A' },
  ],
  'RAM': [
    { code: 'PW7', name: 'Bright White', hex: '#FFFFFF' },
    { code: 'PX8', name: 'Diamond Black Crystal', hex: '#0A0A0A' },
    { code: 'PAU', name: 'Billet Silver Metallic', hex: '#A8A8A8' },
    { code: 'PGG', name: 'Granite Crystal Metallic', hex: '#5A5A5A' },
    { code: 'PRV', name: 'Delmonico Red Pearl', hex: '#6A2A3A' },
    { code: 'PBM', name: 'Patriot Blue Pearl', hex: '#1E3050' },
    { code: 'PR4', name: 'Flame Red', hex: '#CC2222' },
    { code: 'PAE', name: 'Anvil Gray', hex: '#6A6A6A' },
    { code: 'PJD', name: 'Hydro Blue Pearl', hex: '#4A6A7A' },
    { code: 'PWD', name: 'Ivory Pearl Tri-Coat', hex: '#F0E8E0' },
  ],
  'GMC': [
    { code: 'GAZ', name: 'Summit White', hex: '#FFFFFF' },
    { code: 'GBA', name: 'Onyx Black', hex: '#0A0A0A' },
    { code: 'GAN', name: 'Quicksilver Metallic', hex: '#B8B8B8' },
    { code: 'G9K', name: 'Satin Steel Metallic', hex: '#6B6B6B' },
    { code: 'G7C', name: 'Cayenne Red Tintcoat', hex: '#8B2942' },
    { code: 'GXH', name: 'Pacific Blue Metallic', hex: '#1E4070' },
    { code: 'GNO', name: 'Carbon Black Metallic', hex: '#1A1A1A' },
    { code: 'GJI', name: 'Hunter Metallic', hex: '#2A4A2A' },
    { code: 'G1W', name: 'White Frost Tricoat', hex: '#E8E8E8' },
    { code: 'GKZ', name: 'Volcanic Red Tintcoat', hex: '#6A1A2A' },
  ],
  'Default': [
    { code: 'WHT', name: 'White', hex: '#FFFFFF' },
    { code: 'BLK', name: 'Black', hex: '#0A0A0A' },
    { code: 'SLV', name: 'Silver', hex: '#C0C0C0' },
    { code: 'GRY', name: 'Gray', hex: '#6B6B6B' },
    { code: 'RED', name: 'Red', hex: '#CC2222' },
    { code: 'BLU', name: 'Blue', hex: '#1E3050' },
    { code: 'GRN', name: 'Green', hex: '#2A4A2A' },
    { code: 'BRN', name: 'Brown', hex: '#4A3A2A' },
    { code: 'GLD', name: 'Gold', hex: '#B8A060' },
    { code: 'BEG', name: 'Beige', hex: '#D8C8B0' },
  ],
};

const DETAIL_SERVICES = [
  { 
    id: 'full', 
    name: 'Full Detail', 
    description: 'Complete interior & exterior treatment',
    base: 249, 
    category: 'detail',
    icon: Sparkles,
    popular: true
  },
  { 
    id: 'interior', 
    name: 'Interior Detail', 
    description: 'Deep clean & condition all surfaces',
    base: 159, 
    category: 'detail',
    icon: Sun,
    popular: false
  },
  { 
    id: 'exterior', 
    name: 'Exterior Detail', 
    description: 'Paint correction & protection',
    base: 139, 
    category: 'detail',
    icon: Droplets,
    popular: false
  },
];

const OTHER_SERVICES = [
  { 
    id: 'ceramic', 
    name: 'Ceramic Coating', 
    description: 'Long-lasting paint protection',
    price: 799,
    icon: Shield,
    popular: false
  },
  { 
    id: 'ppf', 
    name: 'Paint Protection Film', 
    description: 'Ultimate scratch & chip protection',
    price: 1299,
    icon: Shield,
    popular: true
  },
  { 
    id: 'tint-auto', 
    name: 'Window Tint', 
    description: 'Premium automotive tinting',
    price: 299,
    icon: Sun,
    popular: false
  },
];

// Default time slots - all available, will be updated by API
const TIME_SLOTS = [
  { time: '9:00 AM', available: true },
  { time: '10:00 AM', available: true },
  { time: '11:00 AM', available: true },
  { time: '12:00 PM', available: true },
  { time: '1:00 PM', available: true },
  { time: '2:00 PM', available: true },
  { time: '3:00 PM', available: true },
  { time: '4:00 PM', available: true },
  { time: '5:00 PM', available: true },
];

/* ---------------- STYLES ---------------- */

const styles = {
  page: {
    minHeight: '100vh',
    background: '#000',
    position: 'relative',
    overflow: 'hidden',
    fontFamily: "'Montserrat', sans-serif",
  },
  gradientOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(to bottom, rgba(220, 38, 38, 0.04), transparent 50%, transparent)',
    pointerEvents: 'none',
  },
  glowOrb1: {
    position: 'absolute',
    top: 0,
    left: '25%',
    width: '400px',
    height: '400px',
    background: 'radial-gradient(circle, rgba(220, 38, 38, 0.06), transparent 70%)',
    borderRadius: '50%',
    filter: 'blur(60px)',
    pointerEvents: 'none',
    animation: 'orbFloat1 12s ease-in-out infinite',
  },
  glowOrb2: {
    position: 'absolute',
    bottom: '25%',
    right: '20%',
    width: '300px',
    height: '300px',
    background: 'radial-gradient(circle, rgba(220, 38, 38, 0.03), transparent 70%)',
    borderRadius: '50%',
    filter: 'blur(60px)',
    pointerEvents: 'none',
    animation: 'orbFloat2 15s ease-in-out infinite',
  },
  hero: {
    position: 'relative',
    padding: '100px 24px 40px',
    textAlign: 'center',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 20px',
    borderRadius: '50px',
    background: 'rgba(220, 38, 38, 0.15)',
    border: '1px solid rgba(220, 38, 38, 0.3)',
    color: '#ef4444',
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: '24px',
    fontFamily: "'Montserrat', sans-serif",
  },
  title: {
    fontSize: 'clamp(32px, 6vw, 56px)',
    fontWeight: 700,
    color: '#fff',
    marginBottom: '16px',
    fontFamily: "'Oswald', sans-serif",
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  titleAccent: {
    color: '#dc2626',
  },
  subtitle: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: '18px',
    maxWidth: '600px',
    margin: '0 auto',
    lineHeight: 1.6,
    fontFamily: "'Montserrat', sans-serif",
  },
  progressContainer: {
    maxWidth: '700px',
    margin: '50px auto 0',
    padding: '0 20px',
  },
  progressWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
  },
  progressLine: {
    position: 'absolute',
    left: '20px',
    right: '20px',
    top: '20px',
    height: '3px',
    background: 'rgba(255,255,255,0.08)',
    borderRadius: '3px',
    zIndex: 0,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #dc2626, #ef4444)',
    borderRadius: '2px',
    transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  progressStep: {
    position: 'relative',
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: '0 0 auto',
  },
  progressCircle: (completed, active) => ({
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 700,
    fontFamily: "'Montserrat', sans-serif",
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    background: completed
      ? 'linear-gradient(135deg, #dc2626, #b91c1c)'
      : active
        ? 'rgba(220, 38, 38, 0.2)'
        : '#000',
    border: active && !completed 
      ? '2px solid #dc2626' 
      : completed 
        ? 'none' 
        : '2px solid rgba(255,255,255,0.1)',
    color: completed ? '#fff' : active ? '#dc2626' : 'rgba(255,255,255,0.4)',
    boxShadow: completed 
      ? '0 4px 15px rgba(220, 38, 38, 0.4), 0 0 0 4px rgba(220, 38, 38, 0.1)' 
      : active 
        ? '0 0 0 4px rgba(220, 38, 38, 0.1)' 
        : 'none',
    cursor: 'default',
  }),
  progressLabel: (completed, active) => ({
    marginTop: '10px',
    fontSize: '12px',
    fontWeight: 600,
    fontFamily: "'Montserrat', sans-serif",
    color: completed ? '#dc2626' : active ? '#fff' : 'rgba(255,255,255,0.4)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    transition: 'all 0.3s ease',
  }),
  content: {
    position: 'relative',
    padding: '0 24px 80px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  card: {
    background: 'rgba(255,255,255,0.02)',
    backdropFilter: 'blur(20px)',
    borderRadius: '24px',
    padding: '32px',
    marginBottom: '24px',
    border: '1px solid rgba(255,255,255,0.06)',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
  },
  stepHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '32px',
  },
  stepNumber: {
    width: '48px',
    height: '48px',
    borderRadius: '14px',
    background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: 700,
    color: '#fff',
    boxShadow: '0 10px 25px rgba(220, 38, 38, 0.3)',
  },
  stepTitle: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#fff',
    fontFamily: "'Oswald', sans-serif",
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  formGrid: (cols) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
    gap: '20px',
  }),
  inputWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    minWidth: 0,
  },
  label: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.5)',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontFamily: "'Montserrat', sans-serif",
  },
  required: {
    color: '#ef4444',
  },
  inputContainer: {
    position: 'relative',
    width: '100%',
  },
  inputIcon: {
    position: 'absolute',
    left: '16px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'rgba(255,255,255,0.3)',
  },
  input: (hasIcon) => ({
    width: '100%',
    boxSizing: 'border-box',
    padding: hasIcon ? '16px 16px 16px 48px' : '16px',
    borderRadius: '14px',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    color: '#fff',
    fontSize: '15px',
    fontFamily: "'Montserrat', sans-serif",
    outline: 'none',
    transition: 'all 0.3s ease',
  }),
  select: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '16px',
    paddingRight: '48px',
    borderRadius: '14px',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    color: '#fff',
    fontSize: '15px',
    fontFamily: "'Montserrat', sans-serif",
    outline: 'none',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    appearance: 'none',
  },
  selectArrow: {
    position: 'absolute',
    right: '16px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'rgba(255,255,255,0.4)',
    pointerEvents: 'none',
  },
  locationGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px',
  },
  noOptionsMessage: {
    padding: '40px 20px',
    textAlign: 'center',
    background: 'rgba(239, 68, 68, 0.1)',
    borderRadius: '18px',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    gridColumn: '1 / -1',
    fontFamily: "'Montserrat', sans-serif",
  },
  locationCard: (selected) => ({
    position: 'relative',
    padding: '28px',
    borderRadius: '18px',
    background: selected ? 'rgba(220, 38, 38, 0.15)' : 'rgba(255,255,255,0.03)',
    border: selected ? '2px solid #dc2626' : '1px solid rgba(255,255,255,0.08)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textAlign: 'center',
    boxShadow: selected ? '0 10px 30px rgba(220, 38, 38, 0.2)' : 'none',
  }),
  locationIcon: {
    width: '56px',
    height: '56px',
    borderRadius: '16px',
    background: 'rgba(220, 38, 38, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
  },
  locationLabel: {
    color: '#fff',
    fontWeight: 700,
    fontSize: '18px',
    marginBottom: '6px',
    fontFamily: "'Oswald', sans-serif",
    letterSpacing: '0.5px',
  },
  locationDesc: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: '13px',
    fontFamily: "'Montserrat', sans-serif",
  },
  locationUpcharge: {
    color: '#4ade80',
    fontSize: '14px',
    fontWeight: 500,
    marginTop: '12px',
    fontFamily: "'Montserrat', sans-serif",
  },
  // Pickup & Delivery Styles
  pickupDeliveryContainer: {
    background: 'rgba(255,255,255,0.02)',
    borderRadius: '18px',
    padding: '24px',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  pickupDeliveryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '8px',
  },
  pickupDeliveryTitle: {
    color: '#fff',
    fontWeight: 600,
    fontSize: '16px',
  },
  pickupDeliveryDesc: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: '14px',
    marginBottom: '20px',
    lineHeight: 1.5,
  },
  pickupOptions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
  },
  pickupOptionCard: (selected) => ({
    position: 'relative',
    padding: '20px',
    borderRadius: '14px',
    background: selected ? 'rgba(220, 38, 38, 0.15)' : 'rgba(255,255,255,0.03)',
    border: selected ? '2px solid #dc2626' : '1px solid rgba(255,255,255,0.08)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textAlign: 'center',
    boxShadow: selected ? '0 8px 20px rgba(220, 38, 38, 0.15)' : 'none',
  }),
  pickupOptionTitle: {
    color: '#fff',
    fontWeight: 700,
    fontSize: '15px',
    marginBottom: '4px',
    fontFamily: "'Oswald', sans-serif",
    letterSpacing: '0.5px',
  },
  pickupOptionSubtitle: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: '12px',
    fontFamily: "'Montserrat', sans-serif",
  },
  pickupOptionPrice: {
    color: '#4ade80',
    fontSize: '13px',
    fontWeight: 500,
    marginTop: '8px',
    fontFamily: "'Montserrat', sans-serif",
  },
  distanceNote: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '12px 16px',
    background: 'rgba(220, 38, 38, 0.1)',
    borderRadius: '12px',
    marginBottom: '16px',
    color: 'rgba(255,255,255,0.7)',
    fontSize: '13px',
    lineHeight: 1.5,
  },
  distanceOptions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
  },
  distanceCard: (selected) => ({
    position: 'relative',
    padding: '16px',
    borderRadius: '12px',
    background: selected ? 'rgba(220, 38, 38, 0.15)' : 'rgba(255,255,255,0.03)',
    border: selected ? '2px solid #dc2626' : '1px solid rgba(255,255,255,0.08)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textAlign: 'center',
  }),
  distanceLabel: {
    color: '#fff',
    fontWeight: 700,
    fontSize: '14px',
    marginBottom: '4px',
    fontFamily: "'Oswald', sans-serif",
    letterSpacing: '0.5px',
  },
  distancePrice: {
    color: '#4ade80',
    fontSize: '16px',
    fontWeight: 500,
    fontFamily: "'Montserrat', sans-serif",
  },
  // Mobile Utilities Styles
  utilitiesContainer: {
    background: 'rgba(59, 130, 246, 0.08)',
    borderRadius: '18px',
    padding: '24px',
    border: '1px solid rgba(59, 130, 246, 0.2)',
  },
  utilitiesHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '8px',
  },
  utilitiesTitle: {
    color: '#fff',
    fontWeight: 600,
    fontSize: '16px',
  },
  utilitiesDesc: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: '14px',
    marginBottom: '20px',
    lineHeight: 1.6,
  },
  utilitiesCheckbox: (checked) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '20px',
    borderRadius: '14px',
    background: checked ? 'rgba(34, 197, 94, 0.15)' : 'rgba(255,255,255,0.03)',
    border: checked ? '2px solid #22c55e' : '1px solid rgba(255,255,255,0.1)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textAlign: 'left',
    width: '100%',
  }),
  checkboxBox: (checked) => ({
    width: '28px',
    height: '28px',
    borderRadius: '8px',
    background: checked ? '#22c55e' : 'rgba(255,255,255,0.1)',
    border: checked ? 'none' : '2px solid rgba(255,255,255,0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'all 0.3s ease',
  }),
  checkboxText: {
    flex: 1,
  },
  checkboxLabel: {
    color: '#fff',
    fontWeight: 700,
    fontSize: '15px',
    display: 'block',
    marginBottom: '10px',
    fontFamily: "'Oswald', sans-serif",
    letterSpacing: '0.5px',
  },
  checkboxItems: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  checkboxItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: 'rgba(255,255,255,0.7)',
    fontSize: '13px',
    fontFamily: "'Montserrat', sans-serif",
  },
  utilitiesWarning: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    background: 'rgba(245, 158, 11, 0.1)',
    borderRadius: '12px',
    marginTop: '16px',
    color: '#f59e0b',
    fontSize: '13px',
  },
  vehicleTypeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginTop: '24px',
  },
  vehicleCard: (selected) => ({
    position: 'relative',
    padding: '24px',
    borderRadius: '18px',
    background: selected ? 'rgba(220, 38, 38, 0.15)' : 'rgba(255,255,255,0.03)',
    border: selected ? '2px solid #dc2626' : '1px solid rgba(255,255,255,0.08)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textAlign: 'left',
    boxShadow: selected ? '0 10px 30px rgba(220, 38, 38, 0.2)' : 'none',
  }),
  vehicleIcon: {
    fontSize: '36px',
    marginBottom: '12px',
  },
  vehicleLabel: {
    color: '#fff',
    fontWeight: 700,
    fontSize: '15px',
    marginBottom: '4px',
    fontFamily: "'Oswald', sans-serif",
    letterSpacing: '0.5px',
  },
  vehicleUpcharge: {
    color: '#4ade80',
    fontSize: '14px',
    fontWeight: 500,
    fontFamily: "'Montserrat', sans-serif",
  },
  checkIcon: {
    position: 'absolute',
    top: '12px',
    right: '12px',
    color: '#ef4444',
  },
  colorGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: '12px',
    marginTop: '16px',
  },
  colorCard: (selected) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    borderRadius: '12px',
    background: selected ? 'rgba(220, 38, 38, 0.15)' : 'rgba(255,255,255,0.03)',
    border: selected ? '2px solid #dc2626' : '1px solid rgba(255,255,255,0.08)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  }),
  colorSwatch: (hex) => ({
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    background: hex,
    border: hex === '#FFFFFF' || hex === '#F0F0F0' || hex === '#E8E8E8' || hex === '#F8F8F8' 
      ? '1px solid rgba(255,255,255,0.3)' 
      : 'none',
    flexShrink: 0,
  }),
  colorName: {
    color: '#fff',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: 1.3,
  },
  colorCode: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: '10px',
  },
  sectionTitle: {
    color: '#fff',
    fontWeight: 600,
    fontSize: '16px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  categoryTabs: {
    display: 'flex',
    gap: '12px',
    marginBottom: '24px',
    flexWrap: 'wrap',
  },
  categoryTab: (isSelected) => ({
    padding: '12px 24px',
    background: isSelected ? '#e80200' : 'rgba(255,255,255,0.05)',
    border: isSelected ? '1px solid #e80200' : '1px solid rgba(255,255,255,0.1)',
    color: isSelected ? '#fff' : 'rgba(255,255,255,0.7)',
    fontFamily: "'Oswald', sans-serif",
    fontSize: '14px',
    fontWeight: 600,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  }),
  emptyServices: {
    padding: '40px 20px',
    textAlign: 'center',
    color: 'rgba(255,255,255,0.4)',
    fontFamily: "'Montserrat', sans-serif",
  },
  serviceGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: '16px',
    marginBottom: '32px',
  },
  serviceCard: (selected) => ({
    position: 'relative',
    padding: '24px',
    borderRadius: '18px',
    background: selected ? 'rgba(220, 38, 38, 0.15)' : 'rgba(255,255,255,0.03)',
    border: selected ? '2px solid #dc2626' : '1px solid rgba(255,255,255,0.08)',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textAlign: 'left',
    boxShadow: selected ? '0 10px 30px rgba(220, 38, 38, 0.2)' : 'none',
  }),
  popularBadge: {
    position: 'absolute',
    top: '-10px',
    right: '16px',
    padding: '4px 12px',
    background: '#dc2626',
    color: '#fff',
    fontSize: '11px',
    fontWeight: 700,
    borderRadius: '20px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  serviceIconWrapper: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    background: 'rgba(220, 38, 38, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '16px',
  },
  serviceName: {
    color: '#fff',
    fontWeight: 700,
    fontSize: '16px',
    marginBottom: '6px',
    fontFamily: "'Oswald', sans-serif",
    letterSpacing: '0.5px',
  },
  serviceDesc: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: '13px',
    marginBottom: '12px',
    lineHeight: 1.5,
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    fontFamily: "'Montserrat', sans-serif",
  },
  servicePrice: {
    color: '#fff',
    fontSize: '22px',
    fontWeight: 700,
    fontFamily: "'Oswald', sans-serif",
  },
  twoColGrid: {
    display: 'grid',
    gridTemplateColumns: '3fr 2fr',
    gap: '24px',
  },
  calendarHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  calendarMonth: {
    color: '#fff',
    fontWeight: 600,
    fontSize: '18px',
    fontFamily: "'Montserrat', sans-serif",
  },
  calendarNav: {
    display: 'flex',
    gap: '8px',
  },
  calendarNavBtn: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    background: 'rgba(255,255,255,0.05)',
    border: 'none',
    color: 'rgba(255,255,255,0.5)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
  },
  calendarWeekdays: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '8px',
    marginBottom: '12px',
  },
  calendarWeekday: {
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: 500,
    color: 'rgba(255,255,255,0.4)',
    padding: '8px 0',
    fontFamily: "'Montserrat', sans-serif",
  },
  calendarDays: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '8px',
  },
  calendarDay: (selected, isToday, isPast, isClosed) => ({
    aspectRatio: '1',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 500,
    fontFamily: "'Montserrat', sans-serif",
    cursor: (isPast || isClosed) ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    border: 'none',
    padding: 0,
    background: selected ? '#dc2626'
              : isClosed ? 'rgba(239, 68, 68, 0.1)'
              : isToday ? 'rgba(255,255,255,0.1)'
              : 'rgba(255,255,255,0.03)',
    color: (isPast || isClosed) ? 'rgba(255,255,255,0.3)' : selected ? '#fff' : '#fff',
    ...(isClosed ? { border: '1px solid rgba(239, 68, 68, 0.3)' }
          : isToday && !selected ? { border: '1px solid rgba(220, 38, 38, 0.5)' }
          : {}),
    boxShadow: selected ? '0 8px 20px rgba(220, 38, 38, 0.4)' : 'none',
    textDecoration: isClosed ? 'line-through' : 'none',
  }),
  emptyTimeState: {
    textAlign: 'center',
    padding: '48px 20px',
  },
  emptyTimeIcon: {
    color: 'rgba(255,255,255,0.2)',
    marginBottom: '16px',
  },
  emptyTimeText: {
    color: 'rgba(255,255,255,0.4)',
    fontSize: '14px',
  },
  timeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
  },
  timeSlot: (selected, available) => ({
    padding: '16px',
    borderRadius: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: 500,
    fontFamily: "'Montserrat', sans-serif",
    cursor: available ? 'pointer' : 'not-allowed',
    transition: 'all 0.2s ease',
    background: selected ? '#dc2626' : 'rgba(255,255,255,0.03)',
    color: !available ? 'rgba(255,255,255,0.2)' : '#fff',
    border: selected ? 'none' : '1px solid rgba(255,255,255,0.08)',
    textDecoration: !available ? 'line-through' : 'none',
    boxShadow: selected ? '0 8px 20px rgba(220, 38, 38, 0.4)' : 'none',
  }),
  confirmCard: {
    background: 'linear-gradient(135deg, rgba(127, 29, 29, 0.3), rgba(0,0,0,0.5))',
    backdropFilter: 'blur(20px)',
    borderRadius: '24px',
    padding: '32px',
    marginTop: '32px',
    border: '1px solid rgba(220, 38, 38, 0.3)',
    boxShadow: '0 25px 50px rgba(220, 38, 38, 0.1)',
  },
  confirmContent: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '32px',
  },
  confirmTitle: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#fff',
    marginBottom: '12px',
    fontFamily: "'Oswald', sans-serif",
    textTransform: 'uppercase',
  },
  confirmDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  confirmDetail: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: 'rgba(255,255,255,0.6)',
    fontSize: '14px',
  },
  confirmIcon: {
    color: '#ef4444',
  },
  textarea: {
    width: '100%',
    maxWidth: '400px',
    height: '80px',
    padding: '14px',
    borderRadius: '14px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    color: '#fff',
    fontSize: '14px',
    fontFamily: "'Montserrat', sans-serif",
    resize: 'none',
    outline: 'none',
    boxSizing: 'border-box',
  },
  confirmRight: {
    textAlign: 'right',
  },
  totalLabel: {
    fontSize: '13px',
    color: 'rgba(255,255,255,0.5)',
    marginBottom: '4px',
  },
  totalPrice: {
    fontSize: '40px',
    fontWeight: 700,
    color: '#fff',
    marginBottom: '16px',
  },
  confirmBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '16px 32px',
    borderRadius: '14px',
    background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
    color: '#fff',
    fontSize: '16px',
    fontWeight: 700,
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 10px 30px rgba(220, 38, 38, 0.4)',
    transition: 'all 0.3s ease',
  },
  loadingSpinner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    color: 'rgba(255,255,255,0.5)',
  },
};

/* ---------------- MAIN ---------------- */

export default function BookAppointment() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    address: '',
  });
  const [serviceLocation, setServiceLocation] = useState(null);
  const [pickupDelivery, setPickupDelivery] = useState(null); // null, 'yes', or 'no'
  const [pickupDistance, setPickupDistance] = useState('under15'); // 'under15' or 'over15'
  const [calculatedDistance, setCalculatedDistance] = useState(null); // Actual distance in miles from shop
  const [mobileUtilitiesConfirmed, setMobileUtilitiesConfirmed] = useState(false); // For mobile service water/electric confirmation
  const [vehicle, setVehicle] = useState({ year: '', make: '', model: '' });
  const [vehicleType, setVehicleType] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [emailConsent, setEmailConsent] = useState(false);
  const [availableServices, setAvailableServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [serviceLocations, setServiceLocations] = useState(DEFAULT_SERVICE_LOCATIONS);
  const [businessSettings, setBusinessSettings] = useState(null);
  const [bufferMinutes, setBufferMinutes] = useState(60); // Buffer time between bookings
  const [customVehicle, setCustomVehicle] = useState(false); // Manual entry mode

  // Extra vehicles for multi-vehicle bookings
  const makeExtraVehicleEntry = () => ({
    id: Date.now() + Math.random(),
    vehicle: { make: '', year: '', model: '' },
    vehicleType: null,
    selectedColor: null,
    customVehicle: false,
    selectedServices: [],
    years: [],
    models: [],
    loadingYears: false,
    loadingModels: false,
  });
  const [extraVehicles, setExtraVehicles] = useState([]);

  const updateExtraVehicle = (id, patch) =>
    setExtraVehicles(prev => prev.map(e => e.id === id ? { ...e, ...patch } : e));

  const addExtraVehicle = () => setExtraVehicles(prev => [...prev, makeExtraVehicleEntry()]);
  const removeExtraVehicle = (id) => setExtraVehicles(prev => prev.filter(e => e.id !== id));

  const fetchExtraVehicleYears = async (id, make) => {
    if (!make) { updateExtraVehicle(id, { years: [], models: [], vehicle: { make, year: '', model: '' } }); return; }
    updateExtraVehicle(id, { loadingYears: true });
    try {
      const resp = await fetch(`${API_URL}/api/vehicles/years/${encodeURIComponent(make)}`);
      if (resp.ok) { const data = await resp.json(); updateExtraVehicle(id, { years: data, year: '', models: [] }); }
    } catch (e) { /* ignore */ }
    updateExtraVehicle(id, { loadingYears: false });
  };

  const fetchExtraVehicleModels = async (id, make, year) => {
    if (!make || !year) { updateExtraVehicle(id, { models: [] }); return; }
    updateExtraVehicle(id, { loadingModels: true });
    try {
      const resp = await fetch(`${API_URL}/api/vehicles/models/${encodeURIComponent(make)}?year=${encodeURIComponent(year)}`);
      if (resp.ok) { const data = await resp.json(); updateExtraVehicle(id, { models: data }); }
    } catch (e) { /* ignore */ }
    updateExtraVehicle(id, { loadingModels: false });
  };

  const autoSelectExtraVehicleType = async (id, make, model) => {
    if (!make || !model) return;
    try {
      const resp = await fetch(`${API_URL}/api/vehicles/lookup/${encodeURIComponent(make)}/${encodeURIComponent(model)}`);
      if (resp.ok) {
        const data = await resp.json();
        const typeMap = { 'sedan': 'sedan', 'suv-2row': 'suv-2row', 'suv-3row': 'suv-3row', 'aircraft': 'sedan' };
        const matchId = typeMap[data.vehicle_type] || 'sedan';
        const matchType = VEHICLE_TYPES.find(t => t.id === matchId);
        if (matchType) updateExtraVehicle(id, { vehicleType: matchType });
      }
    } catch (e) { /* ignore */ }
  };

  const toggleExtraVehicleService = (id, service) => {
    setExtraVehicles(prev => prev.map(e => {
      if (e.id !== id) return e;
      const isSelected = e.selectedServices.some(s => s.id === service.id);
      return { ...e, selectedServices: isSelected ? e.selectedServices.filter(s => s.id !== service.id) : [...e.selectedServices, service] };
    }));
  };

  const getExtraPaintColors = (ev) => {
    if (!ev.vehicle.make) return [];
    const makeKey = Object.keys(PAINT_COLORS_BY_MAKE).find(
      key => ev.vehicle.make.toLowerCase().includes(key.toLowerCase()) ||
             key.toLowerCase().includes(ev.vehicle.make.toLowerCase())
    );
    return PAINT_COLORS_BY_MAKE[makeKey] || PAINT_COLORS_BY_MAKE['Default'];
  };

  // API data states
  const [years, setYears] = useState([]);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [loadingMakes, setLoadingMakes] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingYears, setLoadingYears] = useState(false);

  // Google Places Autocomplete ref
  const addressInputRef = useRef(null);

  // Convert 24h time format to 12h AM/PM
  const formatTime24to12 = useCallback((time24) => {
    const [hours, minutes] = time24.split(':').map(Number);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${String(minutes).padStart(2, '0')} ${ampm}`;
  }, []);

  // Convert 12h AM/PM to 24h format for API
  const formatTime12to24 = useCallback((time12) => {
    if (!time12) return '';
    const match = time12.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return time12; // Return as-is if not in expected format
    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const period = match[3].toUpperCase();
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return `${String(hours).padStart(2, '0')}:${minutes}`;
  }, []);

  // Fetch business settings and services from backend
  useEffect(() => {
    const fetchBusinessSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/settings/business`);
        if (response.ok) {
          const data = await response.json();
          setBusinessSettings(data);

          // Update service locations based on enabled settings
          const availableLocations = [];

          if (data.enable_shop_bookings !== false) {
            availableLocations.push({
              id: 'shop',
              label: 'In Shop',
              description: `Drop off at ${data.shop_address || 'our shop location'}`,
              icon: Building2,
              upcharge: 0
            });
          }

          if (data.enable_mobile_bookings !== false) {
            availableLocations.push({
              id: 'mobile',
              label: 'Mobile Service',
              description: data.mobile_service_description || 'We come to you',
              icon: Truck,
              upcharge: data.mobile_service_upcharge ?? 50
            });
          }

          setServiceLocations(availableLocations);
        }
      } catch (err) {
        console.error('Error fetching business settings:', err);
      }
    };
    fetchBusinessSettings();
  }, []);

  // Google Places Autocomplete
  useEffect(() => {
    // Load Google Places API script
    const loadGooglePlacesScript = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        initAutocomplete();
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyCl9Jn6oebsNxjbZHjQe_YqCZ7lho36UZE&libraries=places,geometry`;
      script.async = true;
      script.defer = true;
      script.onload = initAutocomplete;
      document.head.appendChild(script);
    };

    const initAutocomplete = () => {
      if (!addressInputRef.current) return;

      const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
        types: ['address'],
        componentRestrictions: { country: 'us' },
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.formatted_address) {
          setFormData(prev => ({ ...prev, address: place.formatted_address }));

          // Calculate distance from shop if business settings are available
          if (businessSettings && businessSettings.shop_address && place.geometry) {
            calculateDistance(place.geometry.location, businessSettings.shop_address);
          }
        }
      });
    };

    // Calculate distance using Distance Matrix API
    const calculateDistance = (customerLocation, shopAddress) => {
      const service = new window.google.maps.DistanceMatrixService();

      service.getDistanceMatrix(
        {
          origins: [shopAddress],
          destinations: [customerLocation],
          travelMode: window.google.maps.TravelMode.DRIVING,
          unitSystem: window.google.maps.UnitSystem.IMPERIAL,
        },
        (response, status) => {
          if (status === 'OK' && response.rows[0].elements[0].status === 'OK') {
            const distanceInMeters = response.rows[0].elements[0].distance.value;
            const distanceInMiles = (distanceInMeters / 1609.34).toFixed(1); // Convert meters to miles

            setCalculatedDistance(parseFloat(distanceInMiles));

            // Automatically set pickup distance based on calculated distance
            if (parseFloat(distanceInMiles) <= 15) {
              setPickupDistance('under15');
            } else {
              setPickupDistance('over15');
            }
          } else {
            console.error('Distance calculation failed:', status);
            setCalculatedDistance(null);
          }
        }
      );
    };

    loadGooglePlacesScript();
  }, []);

  // Fetch services and categories from backend
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch(`${API_URL}/api/services?active_only=true`);
        if (response.ok) {
          const data = await response.json();
          setAvailableServices(data);
        }
      } catch (err) {
        console.error('Error fetching services:', err);
      }
    };

    const fetchCategories = async () => {
      try {
        const response = await fetch(`${API_URL}/api/categories`);
        if (response.ok) {
          const data = await response.json();
          setCategories(data);
          // Select first non-addon category by default
          const firstNonAddon = data.find(c => !c.is_addon);
          if (firstNonAddon) {
            setSelectedCategory(firstNonAddon.name);
          } else if (data.length > 0) {
            setSelectedCategory(data[0].name);
          }
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };

    fetchServices();
    fetchCategories();
  }, []);

  // Fetch available time slots when date changes
  useEffect(() => {
    const fetchAvailability = async () => {
      if (!selectedDate) {
        setAvailableSlots([]);
        return;
      }
      setLoadingSlots(true);
      try {
        const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;
        const totalDur = selectedServices.reduce((sum, service) => sum + (service.duration_minutes || 60), 0);
        const url = totalDur > 0
          ? `${API_URL}/api/availability/${dateStr}?total_duration=${totalDur}`
          : `${API_URL}/api/availability/${dateStr}`;
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          // Store buffer minutes from API
          if (data.buffer_minutes !== undefined) {
            setBufferMinutes(data.buffer_minutes);
          }
          if (data.available && data.slots && data.slots.length > 0) {
            // Format time slots from 24h to 12h AM/PM format, keeping available_minutes
            const formattedSlots = data.slots.map(slot => ({
              ...slot,
              time: formatTime24to12(slot.time),
              available_minutes: slot.available_minutes || 0
            }));
            setAvailableSlots(formattedSlots);
          } else if (!data.available) {
            // Day is closed
            setAvailableSlots([]);
          } else {
            // Fallback if no slots returned
            setAvailableSlots(TIME_SLOTS.map(t => ({ time: t, available: true, available_minutes: 540 })));
          }
        } else {
          // API error - use fallback
          setAvailableSlots(TIME_SLOTS.map(t => ({ time: t, available: true, available_minutes: 540 })));
        }
      } catch (err) {
        console.error('Error fetching availability:', err);
        // Fallback to default slots on error
        setAvailableSlots(TIME_SLOTS.map(t => ({ time: t, available: true, available_minutes: 540 })));
      } finally {
        setLoadingSlots(false);
      }
    };
    fetchAvailability();
  }, [selectedDate, selectedServices, formatTime24to12]);

  // Handle booking submission
  const handleSubmitBooking = async () => {
    if (!formData.firstName || !formData.email || !formData.phone || selectedServices.length === 0 || !selectedDate || !selectedTime) {
      setSubmitError('Please fill in all required fields');
      return;
    }
    if (!smsConsent && !emailConsent) {
      setSubmitError('Please select at least one communication preference (text or email)');
      return;
    }
    // Validate extra vehicles
    for (let i = 0; i < extraVehicles.length; i++) {
      const ev = extraVehicles[i];
      if (!ev.vehicle.make || !ev.vehicle.model) {
        setSubmitError(`Vehicle ${i + 2}: please select or enter a make and model`);
        return;
      }
      if (ev.selectedServices.length === 0) {
        setSubmitError(`Vehicle ${i + 2} (${ev.vehicle.make} ${ev.vehicle.model}): please select at least one service`);
        return;
      }
    }

    setSubmitting(true);
    setSubmitError('');

    try {
      const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`;

      const allVehicles = [
        {
          vehicle_year: vehicle.year,
          vehicle_make: vehicle.make,
          vehicle_model: vehicle.model,
          vehicle_type: vehicleType?.id || 'sedan',
          vehicle_color: selectedColor?.name || '',
          services: selectedServices.map(s => ({ service_id: s.id, service_name: s.name, base_price: s.base_price, duration_minutes: s.duration_minutes || 60 })),
        },
        ...extraVehicles.map(ev => ({
          vehicle_year: ev.vehicle.year,
          vehicle_make: ev.vehicle.make,
          vehicle_model: ev.vehicle.model,
          vehicle_type: ev.vehicleType?.id || 'sedan',
          vehicle_color: ev.selectedColor?.name || '',
          services: ev.selectedServices.map(s => ({ service_id: s.id, service_name: s.name, base_price: s.base_price, duration_minutes: s.duration_minutes || 60 })),
        })),
      ];

      const serviceNameParts = [
        `${vehicle.make} ${vehicle.model}: ${selectedServices.map(s => s.name).join(' + ')}`,
        ...extraVehicles.map(ev => `${ev.vehicle.make} ${ev.vehicle.model}: ${ev.selectedServices.map(s => s.name).join(' + ')}`),
      ];

      const bookingData = {
        customer_first_name: formData.firstName,
        customer_last_name: formData.lastName,
        customer_phone: formData.phone,
        customer_email: formData.email,
        customer_address: formData.address,
        service_location: serviceLocation?.id || 'shop',
        pickup_delivery: pickupDelivery,
        pickup_distance: pickupDistance,
        vehicle_year: vehicle.year,
        vehicle_make: vehicle.make,
        vehicle_model: vehicle.model,
        vehicle_type: vehicleType?.id || 'sedan',
        vehicle_color: selectedColor?.name || '',
        service_id: selectedServices[0]?.id || '',
        service_name: serviceNameParts.join(' | '),
        booking_date: dateStr,
        booking_time: formatTime12to24(selectedTime),
        total_price: totalPrice,
        total_duration: totalDuration,
        services: selectedServices.map(service => ({
          service_id: service.id,
          service_name: service.name,
          base_price: service.base_price,
          duration_minutes: service.duration_minutes || 60
        })),
        vehicles: allVehicles,
        notes: notes,
        sms_consent: smsConsent,
        email_consent: emailConsent,
      };

      const response = await fetch(`${API_URL}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create booking');
      }

      const booking = await response.json();
      navigate(`/booking-confirmation/${booking.id}`);
    } catch (err) {
      setSubmitError(err.message || 'Failed to submit booking. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Fetch makes from backend on mount
  useEffect(() => {
    const fetchMakes = async () => {
      setLoadingMakes(true);
      try {
        const response = await fetch(`${API_URL}/api/vehicles/makes`);
        if (response.ok) {
          const data = await response.json();
          setMakes(data);
        }
      } catch (error) {
        console.error('Error fetching makes:', error);
      }
      setLoadingMakes(false);
    };
    fetchMakes();
  }, []);

  // Fetch years when make is selected
  const fetchYears = useCallback(async (make) => {
    if (!make) { setYears([]); return; }
    setLoadingYears(true);
    try {
      const response = await fetch(`${API_URL}/api/vehicles/years/${encodeURIComponent(make)}`);
      if (response.ok) {
        const data = await response.json();
        setYears(data);
      }
    } catch (error) {
      console.error('Error fetching years:', error);
      setYears([]);
    }
    setLoadingYears(false);
  }, []);

  // Fetch models when make + year are selected
  const fetchModels = useCallback(async (make, year) => {
    if (!make || !year) { setModels([]); return; }
    setLoadingModels(true);
    try {
      const response = await fetch(`${API_URL}/api/vehicles/models/${encodeURIComponent(make)}?year=${encodeURIComponent(year)}`);
      if (response.ok) {
        const data = await response.json();
        setModels(data);
      }
    } catch (error) {
      console.error('Error fetching models:', error);
      setModels([]);
    }
    setLoadingModels(false);
  }, []);

  // Auto-select vehicle type based on make/model from backend
  const autoSelectVehicleType = useCallback(async (make, model) => {
    if (!make || !model) return;
    try {
      const response = await fetch(`${API_URL}/api/vehicles/lookup/${encodeURIComponent(make)}/${encodeURIComponent(model)}`);
      if (response.ok) {
        const data = await response.json();
        const typeMap = { 'sedan': 'sedan', 'suv-2row': 'suv-2row', 'suv-3row': 'suv-3row', 'aircraft': 'sedan' };
        const matchId = typeMap[data.vehicle_type] || 'sedan';
        const matchType = VEHICLE_TYPES.find(t => t.id === matchId);
        if (matchType) setVehicleType(matchType);
      }
    } catch (error) {
      console.error('Error looking up vehicle type:', error);
    }
  }, []);

  // Effect: when make changes, fetch years for that make
  useEffect(() => {
    if (vehicle.make && !customVehicle) {
      fetchYears(vehicle.make);
    }
  }, [vehicle.make, customVehicle, fetchYears]);

  // Effect: when make + year are both set, fetch models
  useEffect(() => {
    if (vehicle.make && vehicle.year && !customVehicle) {
      fetchModels(vehicle.make, vehicle.year);
    }
  }, [vehicle.year, vehicle.make, customVehicle, fetchModels]);

  // Effect: when model is selected, auto-select vehicle type
  useEffect(() => {
    if (vehicle.make && vehicle.model && !customVehicle) {
      autoSelectVehicleType(vehicle.make, vehicle.model);
    }
  }, [vehicle.model, vehicle.make, customVehicle, autoSelectVehicleType]);

  // Get paint colors for selected make
  const getPaintColors = () => {
    if (!vehicle.make) return [];
    const makeKey = Object.keys(PAINT_COLORS_BY_MAKE).find(
      key => vehicle.make.toLowerCase().includes(key.toLowerCase()) || 
             key.toLowerCase().includes(vehicle.make.toLowerCase())
    );
    return PAINT_COLORS_BY_MAKE[makeKey] || PAINT_COLORS_BY_MAKE['Default'];
  };

  // Map vehicle type ID to vehicle_pricing key
  const getVehiclePricingKey = (typeId) => {
    const map = { 'sedan': 'sedan', 'suv-2row': 'suv_2row', 'suv-3row': 'suv_3row' };
    return map[typeId] || 'sedan';
  };

  // Get vehicle upcharge for a specific service based on selected vehicle type
  const getServiceVehicleUpcharge = (service, vType) => {
    if (!vType || !service.vehicle_pricing) return 0;
    const key = getVehiclePricingKey(vType.id);
    return service.vehicle_pricing[key] || 0;
  };

  // Calculate total price and duration for all selected services
  const locationUpcharge = serviceLocation?.upcharge || 0;
  const vehicleUpcharge = selectedServices.reduce(
    (sum, service) => sum + getServiceVehicleUpcharge(service, vehicleType), 0
  );

  // Pickup & Delivery pricing
  let pickupDeliveryCharge = 0;
  if (serviceLocation?.id === 'shop' && pickupDelivery === 'yes') {
    pickupDeliveryCharge = pickupDistance === 'over15' ? 75 : 50;
  }

  const extraVehiclesPrice = extraVehicles.reduce((sum, ev) =>
    sum + ev.selectedServices.reduce((s, svc) => s + svc.base_price + getServiceVehicleUpcharge(svc, ev.vehicleType), 0), 0);

  const totalPrice = selectedServices.length > 0 && vehicleType
    ? selectedServices.reduce((sum, service) => sum + service.base_price, 0) + vehicleUpcharge + extraVehiclesPrice + locationUpcharge + pickupDeliveryCharge
    : null;

  const extraVehiclesDuration = extraVehicles.reduce((sum, ev) =>
    sum + ev.selectedServices.reduce((s, svc) => s + (svc.duration_minutes || 60), 0), 0);
  const totalDuration = selectedServices.reduce((sum, service) => sum + (service.duration_minutes || 60), 0) + extraVehiclesDuration;

  // Debug logging
  console.log('🔍 Booking Debug:', {
    selectedServices: selectedServices.map(s => s.name),
    totalDuration: totalDuration,
    vehicleType: vehicleType?.label,
    selectedTime,
    totalPrice,
    showConfirmation: !!(selectedTime && totalPrice)
  });

  // Multi-service selection logic
  const canCombineService = (service) => {
    if (selectedServices.length === 0) return true;

    // Get categories of already selected services
    const selectedCategories = selectedServices.map(s => s.category);
    const serviceCategory = categories.find(c => c.name === service.category);

    // Check if this service's category can combine with all selected categories
    for (const selectedCat of selectedCategories) {
      const selectedCategoryObj = categories.find(c => c.name === selectedCat);
      if (!selectedCategoryObj || !serviceCategory) continue;

      // Check bidirectional compatibility
      const canCombineForward = (serviceCategory.can_combine_with || []).includes(selectedCat);
      const canCombineBackward = (selectedCategoryObj.can_combine_with || []).includes(service.category);

      if (!canCombineForward && !canCombineBackward) {
        return false;
      }
    }

    return true;
  };

  // Check if a service has location restrictions
  const hasLocationRestriction = (service) => {
    // Check if service is shop-only (shop_available but NOT mobile_available)
    if (service.shop_available === true && service.mobile_available === false) {
      return 'shop';
    }
    // Check if service is mobile-only (mobile_available but NOT shop_available)
    if (service.mobile_available === true && service.shop_available === false) {
      return 'mobile';
    }
    return null;
  };

  // Check if service doesn't require utilities (for mobile service)
  const checkUtilitiesRequirement = (service) => {
    // If service explicitly doesn't require utilities, return false
    if (service.requires_utilities === false) {
      return false;
    }
    // By default, services require utilities
    return true;
  };

  // State for showing location restriction warning
  const [locationRestrictionWarning, setLocationRestrictionWarning] = useState(null);

  const toggleServiceSelection = (service) => {
    const isSelected = selectedServices.some(s => s.id === service.id);

    if (isSelected) {
      // Remove service and clean up orphaned add-ons
      const remaining = selectedServices.filter(s => s.id !== service.id);
      const remainingMainCategories = remaining
        .filter(s => !categories.find(c => c.name === s.category)?.is_addon)
        .map(s => s.category);
      // Remove add-ons that no longer apply to any remaining main service categories
      const cleaned = remaining.filter(s => {
        const cat = categories.find(c => c.name === s.category);
        if (!cat?.is_addon) return true;
        const appliesTo = s.applies_to_categories || [];
        return appliesTo.some(c => remainingMainCategories.includes(c));
      });
      setSelectedServices(cleaned);
      // Clear warning if no services selected
      if (cleaned.length === 0) {
        setLocationRestrictionWarning(null);
      }
    } else {
      // Check for location restrictions before adding
      const restriction = hasLocationRestriction(service);
      
      if (restriction === 'shop' && serviceLocation?.id === 'mobile') {
        // Service is shop-only but user selected mobile service
        setLocationRestrictionWarning({
          serviceName: service.name,
          requiredLocation: 'In Shop',
          type: 'location',
          message: `"${service.name}" is only available for In Shop service. Please change your service location to "In Shop" to select this service.`
        });
        return; // Don't add the service
      }
      
      if (restriction === 'mobile' && serviceLocation?.id === 'shop') {
        // Service is mobile-only but user selected shop service
        setLocationRestrictionWarning({
          serviceName: service.name,
          requiredLocation: 'Mobile Service',
          type: 'location',
          message: `"${service.name}" is only available for Mobile Service. Please change your service location to "Mobile Service" to select this service.`
        });
        return; // Don't add the service
      }

      // Check utilities requirement for mobile service
      if (serviceLocation?.id === 'mobile' && mobileUtilitiesConfirmed) {
        const requiresUtilities = checkUtilitiesRequirement(service);
        if (!requiresUtilities) {
          // Service doesn't require utilities but user confirmed they have utilities
          // This is just informational - let them know this service doesn't need utilities
          setLocationRestrictionWarning({
            serviceName: service.name,
            type: 'utilities_not_required',
            message: `Good news! "${service.name}" doesn't require water or electricity access, so you're all set for mobile service.`
          });
          // Still add the service - this is just informational
        }
      }

      // Add service if compatible
      if (canCombineService(service)) {
        setSelectedServices([...selectedServices, service]);
        // Only clear warning if it was a blocking warning (location type)
        if (locationRestrictionWarning?.type === 'location') {
          setLocationRestrictionWarning(null);
        }
      }
    }
  };

  // Filter categories to only show compatible ones
  const getAvailableCategories = () => {
    // Filter out add-on categories from the main tabs
    const nonAddonCategories = categories.filter(c => !c.is_addon);

    if (selectedServices.length === 0) {
      return nonAddonCategories;
    }

    return nonAddonCategories.filter(category => {
      // Always show categories that already have selected services
      if (selectedServices.some(s => s.category === category.name)) {
        return true;
      }

      // Check if this category can combine with all selected categories
      const selectedCategories = selectedServices.map(s => s.category);
      return selectedCategories.every(selectedCat => {
        const selectedCategoryObj = categories.find(c => c.name === selectedCat);
        const canCombineForward = (category.can_combine_with || []).includes(selectedCat);
        const canCombineBackward = (selectedCategoryObj?.can_combine_with || []).includes(category.name);
        return canCombineForward || canCombineBackward;
      });
    });
  };

  // Get relevant add-on services based on selected services' categories
  const getRelevantAddons = () => {
    if (selectedServices.length === 0) return [];
    const selectedCategories = selectedServices.map(s => s.category);
    return availableServices.filter(service => {
      const serviceCategory = categories.find(c => c.name === service.category);
      if (!serviceCategory?.is_addon) return false;
      const appliesTo = service.applies_to_categories || [];
      if (appliesTo.length === 0) return false;
      return appliesTo.some(cat => selectedCategories.includes(cat));
    });
  };

  // Get available minutes for the selected time slot
  const getSelectedSlotAvailableMinutes = () => {
    if (!selectedTime) return null;
    const slot = availableSlots.find(s => s.time === selectedTime);
    return slot ? slot.available_minutes : null;
  };

  // Check if a service can fit within the available time (considering buffer)
  const canServiceFitInTime = (service) => {
    const availableMinutes = getSelectedSlotAvailableMinutes();
    if (availableMinutes === null) return true; // No time selected yet, show all services
    
    // Service duration + buffer must fit within available time
    const serviceDuration = service.duration_minutes || 60;
    const currentTotalDuration = selectedServices.reduce((sum, s) => sum + (s.duration_minutes || 60), 0);
    const newTotalDuration = currentTotalDuration + serviceDuration;
    
    return newTotalDuration <= availableMinutes;
  };

  // Filter services that can fit in the selected time slot
  const getServicesForTimeSlot = () => {
    if (!selectedTime) return availableServices;
    
    return availableServices.filter(service => {
      // Check if already selected (always show selected services)
      if (selectedServices.some(s => s.id === service.id)) return true;
      // Check if service can fit in available time
      return canServiceFitInTime(service);
    });
  };

  // Get services hidden due to time constraints (for current category)
  const getHiddenServicesForCategory = (categoryName) => {
    if (!selectedTime || !categoryName) return [];
    const availableMinutes = getSelectedSlotAvailableMinutes();
    if (availableMinutes === null) return [];
    
    return availableServices.filter(service => {
      if (service.category !== categoryName) return false;
      if (selectedServices.some(s => s.id === service.id)) return false; // Don't count selected services
      return !canServiceFitInTime(service);
    });
  };

  const progressSteps = [
    { num: 1, label: 'Vehicle', completed: vehicleType !== null && vehicle.make && selectedColor },
    { num: 2, label: 'Service', completed: selectedServices.length > 0 },
    { num: 3, label: 'Info', completed: formData.firstName && formData.email && formData.address },
    { num: 4, label: 'Location', completed: serviceLocation !== null && (serviceLocation?.id !== 'mobile' || mobileUtilitiesConfirmed) },
    { num: 5, label: 'Schedule', completed: selectedDate && selectedTime },
  ];

  const completedSteps = progressSteps.filter(s => s.completed).length;

  // Responsive handling
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div style={styles.page}>
      {/* Background Effects */}
      <div style={styles.gradientOverlay} />
      <div style={styles.glowOrb1} />
      <div style={styles.glowOrb2} />

      {/* Hero Section */}
      <div style={styles.hero}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div style={styles.badge}>
            <Sparkles size={14} />
            Premium Auto Care
          </div>
          <h1 style={styles.title}>
            BOOK YOUR <span style={styles.titleAccent}>APPOINTMENT</span>
          </h1>
          <p style={styles.subtitle}>
            Experience the finest auto detailing & protection services in Georgia. 
            Your vehicle deserves the best care.
          </p>
        </motion.div>

        {/* Progress Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          style={styles.progressContainer}
        >
          <div style={styles.progressWrapper}>
            {/* Background Line */}
            <div style={styles.progressLine}>
              <motion.div 
                style={styles.progressFill}
                initial={{ width: '0%' }}
                animate={{ width: `${(completedSteps / (progressSteps.length - 1)) * 100}%` }}
                transition={{ duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
              />
            </div>
            
            {/* Steps */}
            {progressSteps.map((step, index) => {
              const isActive = index === completedSteps && !step.completed;
              return (
                <div key={step.num} style={styles.progressStep}>
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                    whileHover={{ scale: 1.05 }}
                    style={styles.progressCircle(step.completed, isActive)}
                  >
                    {step.completed ? (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                      >
                        <CheckCircle2 size={20} />
                      </motion.div>
                    ) : (
                      step.num
                    )}
                  </motion.div>
                  <motion.span 
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.1 + 0.1 }}
                    style={styles.progressLabel(step.completed, isActive)}
                  >
                    {step.label}
                  </motion.span>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>

      {/* Main Content */}
      <div style={styles.content}>
        
        {/* STEP 1 – VEHICLE */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          style={styles.card}
          data-testid="step-1-card"
        >
          <div style={styles.stepHeader}>
            <div style={styles.stepNumber}>1</div>
            <h3 style={styles.stepTitle}>YOUR VEHICLE</h3>
            <Car size={20} style={{ color: '#ef4444', marginLeft: 'auto' }} />
          </div>

          {!customVehicle ? (
            <div style={{ ...styles.formGrid(3), ...(isMobile && { gridTemplateColumns: '1fr' }) }}>
              <SelectField
                label="Make"
                value={vehicle.make}
                onChange={(v) => { setVehicle({...vehicle, make: v, year: '', model: ''}); setModels([]); setSelectedColor(null); setVehicleType(null); }}
                options={makes}
                placeholder={loadingMakes ? "Loading..." : "Select Make"}
                disabled={loadingMakes}
              />
              <SelectField
                label="Year"
                value={vehicle.year}
                onChange={(v) => { setVehicle({...vehicle, year: v, model: ''}); setSelectedColor(null); setVehicleType(null); }}
                options={years}
                placeholder={loadingYears ? "Loading..." : "Select Year"}
                disabled={!vehicle.make || loadingYears}
              />
              <SelectField
                label="Model"
                value={vehicle.model}
                onChange={(v) => setVehicle({...vehicle, model: v})}
                options={models}
                placeholder={loadingModels ? "Loading..." : "Select Model"}
                disabled={!vehicle.year || loadingModels}
              />
            </div>
          ) : (
            <div style={{ ...styles.formGrid(3), ...(isMobile && { gridTemplateColumns: '1fr' }) }}>
              <InputField label="Make" value={vehicle.make} onChange={(v) => setVehicle({...vehicle, make: v})} placeholder="e.g., Toyota" />
              <InputField label="Year" value={vehicle.year} onChange={(v) => setVehicle({...vehicle, year: v})} placeholder="e.g., 2024" />
              <InputField label="Model" value={vehicle.model} onChange={(v) => setVehicle({...vehicle, model: v})} placeholder="e.g., Camry" />
            </div>
          )}
          <button
            onClick={() => {
              setCustomVehicle(!customVehicle);
              setVehicle({ year: '', make: '', model: '' });
              setYears([]);
              setModels([]);
              setSelectedColor(null);
              setVehicleType(null);
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#ef4444',
              fontSize: '13px',
              fontFamily: "'Montserrat', sans-serif",
              cursor: 'pointer',
              padding: '8px 0',
              marginTop: '4px',
              textDecoration: 'underline',
              textUnderlineOffset: '3px',
            }}
          >
            {customVehicle ? '← Back to vehicle list' : "Vehicle not listed? Enter manually"}
          </button>

          {/* Vehicle Type Selection */}
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px', marginTop: '24px', marginBottom: '16px' }}>
            Select your vehicle type:
          </p>
          <div style={styles.vehicleTypeGrid}>
            {VEHICLE_TYPES.map((type) => {
              const typeUpcharge = selectedServices.reduce(
                (sum, service) => sum + getServiceVehicleUpcharge(service, type), 0
              );
              return (
                <VehicleTypeCard
                  key={type.id}
                  type={{ ...type, upcharge: typeUpcharge }}
                  selected={vehicleType?.id === type.id}
                  onClick={() => setVehicleType(type)}
                />
              );
            })}
          </div>

          {/* Color Selection */}
          {vehicle.make && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              transition={{ duration: 0.3 }}
            >
              <div style={{ ...styles.sectionTitle, marginTop: '32px' }}>
                <Palette size={18} style={{ color: '#ef4444' }} />
                Select Your Paint Color
                <span style={{ fontSize: '12px', fontWeight: 400, color: 'rgba(255,255,255,0.4)' }}>
                  ({vehicle.make} Colors)
                </span>
              </div>
              <div style={styles.colorGrid}>
                {getPaintColors().map((color) => (
                  <motion.button
                    key={color.code}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => setSelectedColor(color)}
                    style={styles.colorCard(selectedColor?.code === color.code)}
                    data-testid={`color-${color.code}`}
                  >
                    <div style={styles.colorSwatch(color.hex)} />
                    <div>
                      <div style={styles.colorName}>{color.name}</div>
                      <div style={styles.colorCode}>{color.code}</div>
                    </div>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* STEP 2 – SERVICES */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          style={{ ...styles.card, ...(isMobile && { padding: '20px 16px', borderRadius: '18px' }) }}
          data-testid="step-2-card"
        >
          <div style={{ ...styles.stepHeader, ...(isMobile && { gap: '12px', marginBottom: '20px' }) }}>
            <div style={{ ...styles.stepNumber, ...(isMobile && { width: '36px', height: '36px', fontSize: '14px', borderRadius: '10px' }) }}>2</div>
            <h3 style={{ ...styles.stepTitle, ...(isMobile && { fontSize: '16px' }) }}>SELECT SERVICE(S)</h3>
            <Sparkles size={isMobile ? 18 : 20} style={{ color: '#ef4444', marginLeft: 'auto' }} />
          </div>

          {/* Multi-service info message */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '8px' : '10px',
            padding: isMobile ? '10px 12px' : '12px 16px',
            background: 'rgba(232, 2, 0, 0.1)',
            border: '1px solid rgba(232, 2, 0, 0.3)',
            borderRadius: '12px',
            marginBottom: isMobile ? '16px' : '20px',
          }}>
            <Info size={isMobile ? 16 : 18} style={{ color: '#e80200', flexShrink: 0 }} />
            <p style={{
              margin: 0,
              color: 'rgba(255,255,255,0.8)',
              fontSize: isMobile ? '12px' : '13px',
              lineHeight: 1.5,
              fontFamily: "'Montserrat', sans-serif",
            }}>
              <strong style={{ color: '#e80200' }}>Combine Services:</strong> Select multiple compatible services for one appointment. Click services to add or remove them.
              {selectedServices.length > 0 && (
                <span style={{ color: '#e80200', fontWeight: 600 }}>
                  {' '}({selectedServices.length} selected)
                </span>
              )}
            </p>
          </div>

          {/* Location Restriction Warning */}
          <AnimatePresence>
            {locationRestrictionWarning && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                style={{
                  marginBottom: isMobile ? '16px' : '20px',
                }}
                data-testid="location-restriction-warning"
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: isMobile ? '10px' : '12px',
                  padding: isMobile ? '14px 12px' : '16px',
                  background: locationRestrictionWarning.type === 'utilities_not_required' 
                    ? 'rgba(34, 197, 94, 0.15)' 
                    : 'rgba(245, 158, 11, 0.15)',
                  border: locationRestrictionWarning.type === 'utilities_not_required'
                    ? '2px solid rgba(34, 197, 94, 0.5)'
                    : '2px solid rgba(245, 158, 11, 0.5)',
                  borderRadius: '12px',
                }}>
                  <Info size={isMobile ? 20 : 24} style={{ 
                    color: locationRestrictionWarning.type === 'utilities_not_required' ? '#22c55e' : '#f59e0b', 
                    flexShrink: 0, 
                    marginTop: '2px' 
                  }} />
                  <div style={{ flex: 1 }}>
                    <p style={{
                      margin: '0 0 10px 0',
                      color: locationRestrictionWarning.type === 'utilities_not_required' ? '#22c55e' : '#f59e0b',
                      fontSize: isMobile ? '13px' : '14px',
                      fontWeight: 600,
                      fontFamily: "'Montserrat', sans-serif",
                    }}>
                      {locationRestrictionWarning.type === 'utilities_not_required' 
                        ? 'No Utilities Required' 
                        : 'Service Location Required'}
                    </p>
                    <p style={{
                      margin: '0 0 12px 0',
                      color: 'rgba(255,255,255,0.8)',
                      fontSize: isMobile ? '12px' : '13px',
                      lineHeight: 1.5,
                      fontFamily: "'Montserrat', sans-serif",
                    }}>
                      {locationRestrictionWarning.message}
                    </p>
                    <button
                      onClick={() => setLocationRestrictionWarning(null)}
                      style={{
                        padding: '8px 16px',
                        background: locationRestrictionWarning.type === 'utilities_not_required'
                          ? 'rgba(34, 197, 94, 0.2)'
                          : 'rgba(245, 158, 11, 0.2)',
                        border: locationRestrictionWarning.type === 'utilities_not_required'
                          ? '1px solid rgba(34, 197, 94, 0.5)'
                          : '1px solid rgba(245, 158, 11, 0.5)',
                        borderRadius: '8px',
                        color: locationRestrictionWarning.type === 'utilities_not_required' ? '#22c55e' : '#f59e0b',
                        fontSize: isMobile ? '12px' : '13px',
                        fontWeight: 600,
                        fontFamily: "'Montserrat', sans-serif",
                        cursor: 'pointer',
                      }}
                      data-testid="dismiss-location-warning"
                    >
                      Got it
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Category Tabs */}
          {categories.length > 0 && (
            <div style={{
              ...styles.categoryTabs,
              ...(isMobile && { gap: '8px', marginBottom: '16px', overflowX: 'auto', flexWrap: 'nowrap', WebkitOverflowScrolling: 'touch', msOverflowStyle: 'none', scrollbarWidth: 'none' }),
            }}>
              {getAvailableCategories().map((category) => (
                <motion.button
                  key={category.name}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedCategory(category.name)}
                  style={{
                    ...styles.categoryTab(selectedCategory === category.name),
                    ...(isMobile && { padding: '8px 16px', fontSize: '12px', whiteSpace: 'nowrap', flexShrink: 0 }),
                  }}
                  data-testid={`category-tab-${category.name}`}
                >
                  {category.label}
                </motion.button>
              ))}
            </div>
          )}

          {/* Time constraint warning - only show when there's an actual booking constraint */}
          {selectedTime && getSelectedSlotAvailableMinutes() !== null && getSelectedSlotAvailableMinutes() < 99999 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? '8px' : '10px',
              padding: isMobile ? '10px 12px' : '12px 16px',
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '12px',
              marginBottom: isMobile ? '16px' : '20px',
            }}
            data-testid="time-constraint-info"
            >
              <Clock size={isMobile ? 16 : 18} style={{ color: '#3b82f6', flexShrink: 0 }} />
              <p style={{
                margin: 0,
                color: 'rgba(255,255,255,0.8)',
                fontSize: isMobile ? '12px' : '13px',
                lineHeight: 1.5,
                fontFamily: "'Montserrat', sans-serif",
              }}>
                <strong style={{ color: '#3b82f6' }}>Time Available:</strong> {getSelectedSlotAvailableMinutes()} minutes available at {selectedTime}. 
                Services that exceed this time will not be shown.
                {selectedServices.length > 0 && (
                  <span style={{ color: '#3b82f6', fontWeight: 600 }}>
                    {' '}(Current selection: {selectedServices.reduce((sum, s) => sum + (s.duration_minutes || 60), 0)} min)
                  </span>
                )}
              </p>
            </div>
          )}

          {/* Services filtered by category */}
          {selectedCategory && (
            <>
              <div style={{ ...styles.sectionTitle, ...(isMobile && { fontSize: '14px', marginBottom: '12px' }) }}>
                <Sparkles size={isMobile ? 16 : 18} style={{ color: '#ef4444' }} />
                {categories.find(c => c.name === selectedCategory)?.label || 'Services'}
              </div>
              <div style={{ ...styles.serviceGrid, ...(isMobile && { gridTemplateColumns: '1fr', gap: '12px', marginBottom: '24px' }) }}>
                {availableServices
                  .filter(service => service.category === selectedCategory)
                  .filter(service => canServiceFitInTime(service) || selectedServices.some(s => s.id === service.id))
                  .map((service) => {
                    const serviceVehicleUp = getServiceVehicleUpcharge(service, vehicleType);
                    const price = vehicleType
                      ? service.base_price + serviceVehicleUp + locationUpcharge + pickupDeliveryCharge
                      : 'Select vehicle';
                    return (
                      <ServiceCard
                        key={service.id}
                        service={{
                          ...service,
                          name: service.name,
                          description: service.description,
                          icon: Sparkles
                        }}
                        price={price}
                        selected={selectedServices.some(s => s.id === service.id)}
                        disabled={!canCombineService(service) && !selectedServices.some(s => s.id === service.id)}
                        onClick={() => toggleServiceSelection(service)}
                      />
                    );
                  })}
              </div>
              
              {/* Hidden services due to time constraints */}
              {selectedTime && getHiddenServicesForCategory(selectedCategory).length > 0 && (
                <div style={{
                  marginTop: isMobile ? '12px' : '16px',
                  padding: isMobile ? '12px' : '16px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '12px',
                }}
                data-testid="hidden-services-warning"
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                  }}>
                    <AlertCircle size={isMobile ? 18 : 20} style={{ color: '#ef4444', flexShrink: 0, marginTop: '2px' }} />
                    <div>
                      <p style={{
                        margin: '0 0 8px 0',
                        color: '#ef4444',
                        fontSize: isMobile ? '13px' : '14px',
                        fontWeight: 600,
                        fontFamily: "'Montserrat', sans-serif",
                      }}>
                        {getHiddenServicesForCategory(selectedCategory).length} service{getHiddenServicesForCategory(selectedCategory).length > 1 ? 's' : ''} hidden due to time constraints
                      </p>
                      <p style={{
                        margin: '0 0 10px 0',
                        color: 'rgba(255,255,255,0.7)',
                        fontSize: isMobile ? '12px' : '13px',
                        lineHeight: 1.5,
                        fontFamily: "'Montserrat', sans-serif",
                      }}>
                        The following services require more time than available at {selectedTime} ({getSelectedSlotAvailableMinutes()} min available):
                      </p>
                      <ul style={{
                        margin: 0,
                        paddingLeft: '20px',
                        color: 'rgba(255,255,255,0.6)',
                        fontSize: isMobile ? '11px' : '12px',
                        fontFamily: "'Montserrat', sans-serif",
                      }}>
                        {getHiddenServicesForCategory(selectedCategory).map(service => (
                          <li key={service.id} style={{ marginBottom: '4px' }}>
                            <strong style={{ color: 'rgba(255,255,255,0.8)' }}>{service.name}</strong>
                            <span style={{ color: '#ef4444' }}> ({service.duration_minutes || 60} min needed)</span>
                          </li>
                        ))}
                      </ul>
                      <p style={{
                        margin: '10px 0 0 0',
                        color: 'rgba(255,255,255,0.5)',
                        fontSize: isMobile ? '11px' : '12px',
                        fontStyle: 'italic',
                        fontFamily: "'Montserrat', sans-serif",
                      }}>
                        💡 Tip: Select an earlier time slot or a different date for more availability.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {availableServices.filter(service => service.category === selectedCategory).filter(service => canServiceFitInTime(service) || selectedServices.some(s => s.id === service.id)).length === 0 && (
                <div style={styles.emptyServices}>
                  <p>{selectedTime ? 'No services available that fit in the selected time slot.' : 'No services available in this category.'}</p>
                </div>
              )}
            </>
          )}

          {/* Add-On Services Section */}
          {selectedServices.length > 0 && getRelevantAddons().length > 0 && (
            <div style={{ marginTop: isMobile ? '16px' : '24px' }}>
              <div style={{ ...styles.sectionTitle, ...(isMobile && { fontSize: '14px', marginBottom: '12px' }) }}>
                <Zap size={isMobile ? 16 : 18} style={{ color: '#ef4444' }} />
                Add-On Services
              </div>
              <p style={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: isMobile ? '12px' : '13px',
                color: 'rgba(255,255,255,0.5)',
                marginBottom: isMobile ? '12px' : '16px',
                marginTop: '-8px',
              }}>
                Enhance your detail with these add-ons
              </p>
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                gap: '12px',
              }}>
                {getRelevantAddons().map((addon) => {
                  const isSelected = selectedServices.some(s => s.id === addon.id);
                  const addonUpcharge = getServiceVehicleUpcharge(addon, vehicleType);
                  const addonPrice = vehicleType
                    ? addon.base_price + addonUpcharge + locationUpcharge + pickupDeliveryCharge
                    : 'Select vehicle';
                  return (
                    <motion.button
                      key={addon.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedServices(selectedServices.filter(s => s.id !== addon.id));
                        } else {
                          setSelectedServices([...selectedServices, addon]);
                        }
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: isMobile ? '10px' : '14px',
                        padding: isMobile ? '12px' : '16px',
                        borderRadius: isMobile ? '12px' : '14px',
                        background: isSelected ? 'rgba(220, 38, 38, 0.15)' : 'rgba(255,255,255,0.03)',
                        border: isSelected ? '2px solid #dc2626' : '1px solid rgba(255,255,255,0.08)',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        textAlign: 'left',
                        boxShadow: isSelected ? '0 6px 20px rgba(220, 38, 38, 0.2)' : 'none',
                        width: '100%',
                      }}
                      data-testid={`addon-${addon.id}`}
                    >
                      <div style={{
                        width: isMobile ? '20px' : '24px',
                        height: isMobile ? '20px' : '24px',
                        borderRadius: '6px',
                        border: isSelected ? '2px solid #dc2626' : '2px solid rgba(255,255,255,0.2)',
                        background: isSelected ? '#dc2626' : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        transition: 'all 0.2s ease',
                      }}>
                        {isSelected && <CheckCircle2 size={isMobile ? 12 : 14} style={{ color: '#fff' }} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontFamily: "'Oswald', sans-serif",
                          fontSize: isMobile ? '13px' : '15px',
                          fontWeight: 600,
                          color: '#fff',
                          letterSpacing: '0.5px',
                          textTransform: 'uppercase',
                          marginBottom: '4px',
                        }}>
                          {addon.name}
                        </div>
                        {addon.description && (
                          <div style={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: isMobile ? '11px' : '12px',
                            color: 'rgba(255,255,255,0.45)',
                            lineHeight: '1.4',
                          }}>
                            {addon.description.split('\n').filter(l => l.trim()).slice(0, 2).map((line, i) => (
                              <div key={i} style={{ display: 'flex', gap: '4px', alignItems: 'flex-start' }}>
                                <span style={{ color: '#ef4444', flexShrink: 0, fontSize: '10px' }}>•</span>
                                <span>{line.trim()}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div style={{
                        fontFamily: "'Oswald', sans-serif",
                        fontSize: isMobile ? '14px' : '16px',
                        fontWeight: 700,
                        color: isSelected ? '#ef4444' : 'rgba(255,255,255,0.7)',
                        whiteSpace: 'nowrap',
                      }}>
                        {typeof addonPrice === 'number' ? `+$${fmt(addonPrice)}` : addonPrice}
                      </div>
                    </motion.button>
                  );
                })}
              </div>
            </div>
          )}
          {/* Additional Vehicles Section */}
          <div style={{ marginTop: '32px', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: '28px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: extraVehicles.length > 0 ? '20px' : '0' }}>
              <Car size={18} style={{ color: '#e80200', flexShrink: 0 }} />
              <span style={{ fontFamily: "'Oswald', sans-serif", fontSize: '15px', fontWeight: 600, color: '#fff', letterSpacing: '1px', textTransform: 'uppercase' }}>
                Additional Vehicles
              </span>
              <span style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>
                — booking multiple vehicles at once?
              </span>
            </div>

            {extraVehicles.map((ev, idx) => (
              <div key={ev.id} style={{ marginBottom: '24px', padding: '20px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px' }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: 'rgba(232,2,0,0.15)', border: '1px solid rgba(232,2,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Oswald', sans-serif", fontSize: '13px', fontWeight: 700, color: '#e80200' }}>{idx + 2}</div>
                    <span style={{ fontFamily: "'Oswald', sans-serif", fontSize: '14px', fontWeight: 600, color: '#fff', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                      Vehicle {idx + 2}
                      {ev.vehicle.make && ev.vehicle.model ? ` — ${ev.vehicle.make} ${ev.vehicle.model}` : ''}
                    </span>
                  </div>
                  <button onClick={() => removeExtraVehicle(ev.id)} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', width: '28px', height: '28px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', lineHeight: 1 }}>×</button>
                </div>

                {/* Vehicle Picker */}
                {!ev.customVehicle ? (
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '12px', marginBottom: '12px' }}>
                    <SelectField label="Make" value={ev.vehicle.make}
                      onChange={(v) => { updateExtraVehicle(ev.id, { vehicle: { make: v, year: '', model: '' }, vehicleType: null, selectedColor: null, years: [], models: [] }); fetchExtraVehicleYears(ev.id, v); }}
                      options={makes} placeholder={loadingMakes ? 'Loading...' : 'Select Make'} disabled={loadingMakes} />
                    <SelectField label="Year" value={ev.vehicle.year}
                      onChange={(v) => { updateExtraVehicle(ev.id, { vehicle: { ...ev.vehicle, year: v, model: '' }, vehicleType: null, models: [] }); fetchExtraVehicleModels(ev.id, ev.vehicle.make, v); }}
                      options={ev.years} placeholder={ev.loadingYears ? 'Loading...' : 'Select Year'} disabled={!ev.vehicle.make || ev.loadingYears} />
                    <SelectField label="Model" value={ev.vehicle.model}
                      onChange={(v) => { updateExtraVehicle(ev.id, { vehicle: { ...ev.vehicle, model: v } }); autoSelectExtraVehicleType(ev.id, ev.vehicle.make, v); }}
                      options={ev.models} placeholder={ev.loadingModels ? 'Loading...' : 'Select Model'} disabled={!ev.vehicle.year || ev.loadingModels} />
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '12px', marginBottom: '12px' }}>
                    <InputField label="Make" value={ev.vehicle.make} onChange={(v) => updateExtraVehicle(ev.id, { vehicle: { ...ev.vehicle, make: v } })} placeholder="e.g., Toyota" />
                    <InputField label="Year" value={ev.vehicle.year} onChange={(v) => updateExtraVehicle(ev.id, { vehicle: { ...ev.vehicle, year: v } })} placeholder="e.g., 2024" />
                    <InputField label="Model" value={ev.vehicle.model} onChange={(v) => updateExtraVehicle(ev.id, { vehicle: { ...ev.vehicle, model: v } })} placeholder="e.g., Camry" />
                  </div>
                )}

                <button onClick={() => updateExtraVehicle(ev.id, { customVehicle: !ev.customVehicle, vehicle: { make: '', year: '', model: '' }, years: [], models: [] })}
                  style={{ background: 'none', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.5)', padding: '6px 14px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer', fontFamily: "'Montserrat', sans-serif", marginBottom: '16px' }}>
                  {ev.customVehicle ? '← Back to vehicle list' : 'Vehicle not listed? Enter manually'}
                </button>

                {/* Vehicle Type */}
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', marginBottom: '12px', fontFamily: "'Montserrat', sans-serif" }}>Vehicle type:</p>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: '8px', marginBottom: '16px' }}>
                  {VEHICLE_TYPES.map(type => (
                    <button key={type.id} onClick={() => updateExtraVehicle(ev.id, { vehicleType: type })}
                      style={{ padding: '10px 8px', borderRadius: '10px', border: ev.vehicleType?.id === type.id ? '2px solid #e80200' : '1px solid rgba(255,255,255,0.1)', background: ev.vehicleType?.id === type.id ? 'rgba(232,2,0,0.12)' : 'rgba(255,255,255,0.03)', color: ev.vehicleType?.id === type.id ? '#fff' : 'rgba(255,255,255,0.5)', cursor: 'pointer', fontFamily: "'Oswald', sans-serif", fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {type.label}
                    </button>
                  ))}
                </div>

                {/* Color (optional) */}
                {ev.vehicle.make && getExtraPaintColors(ev).length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', marginBottom: '10px', fontFamily: "'Montserrat', sans-serif" }}>Paint color (optional):</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {getExtraPaintColors(ev).map(color => (
                        <button key={color.code} onClick={() => updateExtraVehicle(ev.id, { selectedColor: ev.selectedColor?.code === color.code ? null : color })}
                          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderRadius: '8px', border: ev.selectedColor?.code === color.code ? '1px solid #e80200' : '1px solid rgba(255,255,255,0.1)', background: ev.selectedColor?.code === color.code ? 'rgba(232,2,0,0.1)' : 'rgba(255,255,255,0.03)', cursor: 'pointer' }}>
                          <div style={{ width: '14px', height: '14px', borderRadius: '50%', background: color.hex, border: '1px solid rgba(255,255,255,0.2)', flexShrink: 0 }} />
                          <span style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', color: 'rgba(255,255,255,0.7)' }}>{color.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Services for this vehicle */}
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', marginBottom: '12px', fontFamily: "'Montserrat', sans-serif" }}>
                  Services for this vehicle
                  {ev.selectedServices.length > 0 && <span style={{ color: '#e80200', fontWeight: 600 }}> ({ev.selectedServices.length} selected)</span>}:
                </p>
                {categories.filter(c => !c.is_addon).map(cat => {
                  const catServices = availableServices.filter(s => s.category === cat.name);
                  if (catServices.length === 0) return null;
                  return (
                    <div key={cat.name} style={{ marginBottom: '12px' }}>
                      <p style={{ fontFamily: "'Oswald', sans-serif", fontSize: '12px', color: '#e80200', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '8px' }}>{cat.label}</p>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: '8px' }}>
                        {catServices.map(svc => {
                          const isSelected = ev.selectedServices.some(s => s.id === svc.id);
                          const svcPrice = ev.vehicleType
                            ? svc.base_price + getServiceVehicleUpcharge(svc, ev.vehicleType)
                            : svc.base_price;
                          return (
                            <button key={svc.id} onClick={() => toggleExtraVehicleService(ev.id, svc)}
                              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: '10px', border: isSelected ? '1px solid #e80200' : '1px solid rgba(255,255,255,0.08)', background: isSelected ? 'rgba(232,2,0,0.1)' : 'rgba(255,255,255,0.03)', cursor: 'pointer', textAlign: 'left', gap: '8px' }}>
                              <div>
                                <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: '13px', fontWeight: 600, color: isSelected ? '#fff' : 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{svc.name}</div>
                                {svc.duration_minutes && <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginTop: '2px' }}>{svc.duration_minutes} min</div>}
                              </div>
                              <div style={{ fontFamily: "'Oswald', sans-serif", fontSize: '14px', fontWeight: 700, color: isSelected ? '#e80200' : 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap' }}>
                                ${fmt(svcPrice)}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}

            <button onClick={addExtraVehicle}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', background: 'transparent', border: '1px dashed rgba(232,2,0,0.4)', borderRadius: '12px', color: 'rgba(232,2,0,0.8)', cursor: 'pointer', fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 600, width: '100%', justifyContent: 'center', marginTop: extraVehicles.length > 0 ? '0' : '12px', transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(232,2,0,0.06)'; e.currentTarget.style.borderColor = 'rgba(232,2,0,0.7)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(232,2,0,0.4)'; }}>
              <span style={{ fontSize: '18px', lineHeight: 1 }}>+</span>
              Add Another Vehicle
            </button>
          </div>
        </motion.div>

        {/* STEP 3 – CUSTOMER INFO */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          style={styles.card}
          data-testid="step-3-card"
        >
          <div style={styles.stepHeader}>
            <div style={styles.stepNumber}>3</div>
            <h3 style={styles.stepTitle}>YOUR INFORMATION</h3>
            <User size={20} style={{ color: '#ef4444', marginLeft: 'auto' }} />
          </div>
          
          <div style={{ ...styles.formGrid(2), ...(isMobile && { gridTemplateColumns: '1fr' }) }}>
            <InputField 
              label="First Name" 
              icon={User}
              value={formData.firstName}
              onChange={(v) => setFormData({...formData, firstName: v})}
              placeholder="John"
              required
            />
            <InputField 
              label="Last Name" 
              icon={User}
              value={formData.lastName}
              onChange={(v) => setFormData({...formData, lastName: v})}
              placeholder="Doe"
              required
            />
            <InputField 
              label="Phone" 
              icon={Phone}
              value={formData.phone}
              onChange={(v) => setFormData({...formData, phone: v})}
              placeholder="(555) 123-4567"
              type="tel"
              required
            />
            <InputField 
              label="Email" 
              icon={Mail}
              value={formData.email}
              onChange={(v) => setFormData({...formData, email: v})}
              placeholder="john@example.com"
              type="email"
              required
            />
          </div>
          <div style={{ marginTop: '20px' }}>
            <InputField
              label="Address"
              icon={MapPin}
              value={formData.address}
              onChange={(v) => setFormData({...formData, address: v})}
              placeholder="123 Main St, Marietta, GA 30060"
              required
              inputRef={addressInputRef}
            />
          </div>
        </motion.div>

        {/* STEP 4 – SERVICE LOCATION */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          style={styles.card}
          data-testid="step-2-location-card"
        >
          <div style={styles.stepHeader}>
            <div style={styles.stepNumber}>4</div>
            <h3 style={styles.stepTitle}>SERVICE LOCATION</h3>
            <MapPin size={20} style={{ color: '#ef4444', marginLeft: 'auto' }} />
          </div>

          <div style={{ ...styles.locationGrid, ...(isMobile && { gridTemplateColumns: '1fr' }) }}>
            {serviceLocations.length === 0 ? (
              <div style={styles.noOptionsMessage}>
                <p style={{ color: '#ef4444', margin: 0 }}>No booking options are currently available. Please contact us directly.</p>
              </div>
            ) : serviceLocations.map((location) => {
              const Icon = location.icon;
              return (
                <motion.button
                  key={location.id}
                  whileHover={{ scale: 1.02, y: -4 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setServiceLocation(location);
                    // Reset pickup/delivery when changing location
                    if (location.id !== 'shop') {
                      setPickupDelivery(null);
                      setPickupDistance('under15');
                    }
                    // Reset mobile utilities when changing location
                    if (location.id !== 'mobile') {
                      setMobileUtilitiesConfirmed(false);
                    }
                  }}
                  style={styles.locationCard(serviceLocation?.id === location.id)}
                  data-testid={`location-${location.id}`}
                >
                  {serviceLocation?.id === location.id && (
                    <CheckCircle2 size={20} style={styles.checkIcon} />
                  )}
                  <div style={styles.locationIcon}>
                    <Icon size={28} style={{ color: '#ef4444' }} />
                  </div>
                  <div style={styles.locationLabel}>{location.label}</div>
                  <div style={styles.locationDesc}>{location.description}</div>
                  {location.upcharge > 0 && (
                    <div style={styles.locationUpcharge}>+${location.upcharge}</div>
                  )}
                </motion.button>
              );
            })
          }
          </div>

          {/* Mobile Service Utilities Requirement - Only shows when Mobile is selected */}
          <AnimatePresence>
            {serviceLocation?.id === 'mobile' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                style={{ marginTop: '24px' }}
              >
                <div style={styles.utilitiesContainer}>
                  <div style={styles.utilitiesHeader}>
                    <Droplets size={20} style={{ color: '#3b82f6' }} />
                    <span style={styles.utilitiesTitle}>Utility Access Required</span>
                  </div>
                  <p style={styles.utilitiesDesc}>
                    For mobile detailing, we require access to <strong>water</strong> and <strong>electrical hookups</strong> at your location to complete the job properly.
                  </p>
                  
                  <motion.button
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    onClick={() => setMobileUtilitiesConfirmed(!mobileUtilitiesConfirmed)}
                    style={styles.utilitiesCheckbox(mobileUtilitiesConfirmed)}
                    data-testid="utilities-confirm"
                  >
                    <div style={styles.checkboxBox(mobileUtilitiesConfirmed)}>
                      {mobileUtilitiesConfirmed && <CheckCircle2 size={18} style={{ color: '#fff' }} />}
                    </div>
                    <div style={styles.checkboxText}>
                      <span style={styles.checkboxLabel}>Yes, I confirm I have easy access to:</span>
                      <div style={styles.checkboxItems}>
                        <span style={styles.checkboxItem}>
                          <Droplets size={14} style={{ color: '#3b82f6' }} /> Water hookup / hose connection
                        </span>
                        <span style={styles.checkboxItem}>
                          <Zap size={14} style={{ color: '#eab308' }} /> Electrical outlet (standard 120V)
                        </span>
                      </div>
                    </div>
                  </motion.button>

                  {!mobileUtilitiesConfirmed && (
                    <div style={styles.utilitiesWarning}>
                      <Info size={16} style={{ color: '#f59e0b', flexShrink: 0 }} />
                      <span>Please confirm utility access to proceed with mobile service booking.</span>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Pickup & Delivery Option - Only shows when In Shop is selected */}
          <AnimatePresence>
            {serviceLocation?.id === 'shop' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                style={{ marginTop: '24px' }}
              >
                <div style={styles.pickupDeliveryContainer}>
                  <div style={styles.pickupDeliveryHeader}>
                    <CarFront size={20} style={{ color: '#ef4444' }} />
                    <span style={styles.pickupDeliveryTitle}>Pickup & Delivery Service</span>
                  </div>
                  <p style={styles.pickupDeliveryDesc}>
                    Don't have time to drop off? We'll pick up your vehicle and deliver it back to you!
                  </p>

                  <div style={styles.pickupOptions}>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setPickupDelivery('no')}
                      style={styles.pickupOptionCard(pickupDelivery === 'no')}
                      data-testid="pickup-no"
                    >
                      {pickupDelivery === 'no' && (
                        <CheckCircle2 size={18} style={styles.checkIcon} />
                      )}
                      <div style={styles.pickupOptionTitle}>No Thanks</div>
                      <div style={styles.pickupOptionSubtitle}>I'll drop off myself</div>
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setPickupDelivery('yes')}
                      style={styles.pickupOptionCard(pickupDelivery === 'yes')}
                      data-testid="pickup-yes"
                    >
                      {pickupDelivery === 'yes' && (
                        <CheckCircle2 size={18} style={styles.checkIcon} />
                      )}
                      <div style={styles.pickupOptionTitle}>Yes, Please!</div>
                      <div style={styles.pickupOptionSubtitle}>Pick up & deliver my car</div>
                      <div style={styles.pickupOptionPrice}>Starting at +$50</div>
                    </motion.button>
                  </div>

                  {/* Distance Selection - Only shows when pickup is selected */}
                  <AnimatePresence>
                    {pickupDelivery === 'yes' && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        style={{ marginTop: '16px' }}
                      >
                        <div style={styles.distanceNote}>
                          <Info size={16} style={{ color: '#ef4444', flexShrink: 0 }} />
                          <span>
                            {calculatedDistance !== null
                              ? `Distance from our Marietta location: ${calculatedDistance} miles`
                              : 'Select the distance from our Marietta location to your address:'}
                          </span>
                        </div>
                        <div style={styles.distanceOptions}>
                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => setPickupDistance('under15')}
                            style={styles.distanceCard(pickupDistance === 'under15')}
                            data-testid="distance-under15"
                          >
                            {pickupDistance === 'under15' && (
                              <CheckCircle2 size={16} style={{ ...styles.checkIcon, top: '8px', right: '8px' }} />
                            )}
                            <div style={styles.distanceLabel}>Under 15 miles</div>
                            <div style={styles.distancePrice}>+$50</div>
                          </motion.button>

                          <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => setPickupDistance('over15')}
                            style={styles.distanceCard(pickupDistance === 'over15')}
                            data-testid="distance-over15"
                          >
                            {pickupDistance === 'over15' && (
                              <CheckCircle2 size={16} style={{ ...styles.checkIcon, top: '8px', right: '8px' }} />
                            )}
                            <div style={styles.distanceLabel}>Over 15 miles</div>
                            <div style={styles.distancePrice}>+$75</div>
                          </motion.button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
        {/* STEP 5 & 6 – DATE & TIME */}
        <div style={{ ...styles.twoColGrid, ...(isMobile && { gridTemplateColumns: '1fr' }) }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            style={{ ...styles.card, ...(isMobile && { padding: '20px 16px', borderRadius: '18px' }) }}
            data-testid="step-5-card"
          >
            <div style={{ ...styles.stepHeader, ...(isMobile && { gap: '12px', marginBottom: '20px' }) }}>
              <div style={{ ...styles.stepNumber, ...(isMobile && { width: '36px', height: '36px', fontSize: '14px', borderRadius: '10px' }) }}>5</div>
              <h3 style={{ ...styles.stepTitle, ...(isMobile && { fontSize: '16px' }) }}>SELECT A DATE</h3>
              <Calendar size={isMobile ? 18 : 20} style={{ color: '#ef4444', marginLeft: 'auto' }} />
            </div>
            <CalendarPicker selected={selectedDate} onSelect={setSelectedDate} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            style={{ ...styles.card, ...(isMobile && { padding: '20px 16px', borderRadius: '18px' }) }}
            data-testid="step-6-card"
          >
            <div style={{ ...styles.stepHeader, ...(isMobile && { gap: '12px', marginBottom: '20px' }) }}>
              <div style={{ ...styles.stepNumber, ...(isMobile && { width: '36px', height: '36px', fontSize: '14px', borderRadius: '10px' }) }}>6</div>
              <h3 style={{ ...styles.stepTitle, ...(isMobile && { fontSize: '16px' }) }}>SELECT A TIME</h3>
              <Clock size={isMobile ? 18 : 20} style={{ color: '#ef4444', marginLeft: 'auto' }} />
            </div>
            {!selectedDate ? (
              <div style={styles.emptyTimeState}>
                <Calendar size={isMobile ? 36 : 48} style={styles.emptyTimeIcon} />
                <p style={styles.emptyTimeText}>Select a date to view available times</p>
              </div>
            ) : (
              <TimeSlotPicker
                slots={availableSlots}
                selected={selectedTime}
                onSelect={setSelectedTime}
              />
            )}
          </motion.div>
        </div>

        {/* CONFIRMATION */}
        <AnimatePresence>
          {selectedTime && totalPrice && (
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.5 }}
              style={{ ...styles.confirmCard, ...(isMobile && { padding: '20px 16px', borderRadius: '18px', marginTop: '24px' }) }}
              data-testid="confirmation-card"
            >
              <div style={{
                ...styles.confirmContent,
                ...(isMobile && { flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '24px' }),
              }}>
                <div style={isMobile ? { width: '100%', textAlign: 'center' } : undefined}>
                  <h3 style={{ ...styles.confirmTitle, ...(isMobile && { fontSize: '18px', marginBottom: '16px' }) }}>CONFIRM YOUR BOOKING</h3>
                  <div style={{ ...styles.confirmDetails, ...(isMobile && { alignItems: 'center' }) }}>
                    {selectedServices.map((service, idx) => (
                      <div key={service.id} style={{ ...styles.confirmDetail, ...(isMobile && { fontSize: '13px', justifyContent: 'center' }) }}>
                        <Sparkles size={isMobile ? 14 : 16} style={styles.confirmIcon} />
                        {service.name} ({service.duration_minutes || 60} min)
                      </div>
                    ))}
                    <div style={{ ...styles.confirmDetail, ...(isMobile && { fontSize: '13px', justifyContent: 'center' }) }}>
                      <MapPin size={isMobile ? 14 : 16} style={styles.confirmIcon} />
                      {serviceLocation?.label}
                      {serviceLocation?.id === 'mobile' && locationUpcharge > 0 && (
                        <span style={{ color: '#ef4444' }}> (+${locationUpcharge} fee)</span>
                      )}
                      {serviceLocation?.id === 'shop' && pickupDelivery === 'yes' && (
                        <span style={{ color: '#ef4444' }}> + Pickup & Delivery ({pickupDistance === 'over15' ? '>15mi' : '<15mi'})</span>
                      )}
                    </div>
                    <div style={{ ...styles.confirmDetail, ...(isMobile && { fontSize: '13px', justifyContent: 'center', flexWrap: 'wrap' }) }}>
                      <Calendar size={isMobile ? 14 : 16} style={styles.confirmIcon} />
                      {selectedDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })} at {selectedTime}
                    </div>
                    {vehicleType && (
                      <div style={{ ...styles.confirmDetail, ...(isMobile && { fontSize: '13px', justifyContent: 'center' }) }}>
                        <Car size={isMobile ? 14 : 16} style={styles.confirmIcon} />
                        {vehicle.year} {vehicle.make} {vehicle.model} ({vehicleType.label})
                      </div>
                    )}
                    {selectedColor && (
                      <div style={{ ...styles.confirmDetail, ...(isMobile && { fontSize: '13px', justifyContent: 'center' }) }}>
                        <Palette size={isMobile ? 14 : 16} style={styles.confirmIcon} />
                        {selectedColor.name} ({selectedColor.code})
                      </div>
                    )}
                    {serviceLocation?.id === 'mobile' && mobileUtilitiesConfirmed && (
                      <div style={{ ...styles.confirmDetail, ...(isMobile && { fontSize: '13px', justifyContent: 'center' }) }}>
                        <CheckCircle2 size={isMobile ? 14 : 16} style={{ color: '#22c55e' }} />
                        <span style={{ color: '#22c55e' }}>Water & Electric Access Confirmed</span>
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ ...styles.confirmRight, ...(isMobile && { textAlign: 'center', width: '100%' }) }}>
                  {/* Price Breakdown */}
                  <div style={{ marginBottom: '16px', textAlign: isMobile ? 'center' : 'right' }}>
                    {selectedServices.map((service) => {
                      const svcVehicleUp = getServiceVehicleUpcharge(service, vehicleType);
                      return (
                        <div key={service.id} style={{ fontSize: isMobile ? '12px' : '13px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>
                          {service.name}: ${fmt(service.base_price + svcVehicleUp)}
                          {svcVehicleUp > 0 && (
                            <span style={{ color: 'rgba(255,255,255,0.3)' }}> (incl. +${fmt(svcVehicleUp)} {vehicleType?.label})</span>
                          )}
                        </div>
                      );
                    })}
                    {locationUpcharge > 0 && (
                      <div style={{ fontSize: isMobile ? '12px' : '13px', color: '#ef4444', marginBottom: '4px' }}>
                        Mobile Service Fee: +${fmt(locationUpcharge)}
                      </div>
                    )}
                    {pickupDeliveryCharge > 0 && (
                      <div style={{ fontSize: isMobile ? '12px' : '13px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>
                        Pickup & Delivery: +${fmt(pickupDeliveryCharge)}
                      </div>
                    )}
                    <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: '8px', paddingTop: '8px' }} />
                  </div>
                  <div style={styles.totalLabel}>Total</div>
                  <div style={{ ...styles.totalPrice, ...(isMobile && { fontSize: '32px', marginBottom: '12px' }) }}>${fmt(totalPrice)}</div>
                  {submitError && (
                    <div style={{ color: '#ef4444', fontSize: '13px', marginBottom: '12px', fontFamily: "'Montserrat', sans-serif" }}>
                      {submitError}
                    </div>
                  )}
                  <motion.button
                    whileHover={{ scale: submitting ? 1 : 1.03 }}
                    whileTap={{ scale: submitting ? 1 : 0.97 }}
                    onClick={handleSubmitBooking}
                    disabled={submitting}
                    style={{
                      ...styles.confirmBtn,
                      opacity: submitting ? 0.7 : 1,
                      cursor: submitting ? 'not-allowed' : 'pointer',
                      ...(isMobile && { width: '100%', padding: '14px 24px', fontSize: '15px' }),
                    }}
                    data-testid="confirm-appointment-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                        Booking...
                      </>
                    ) : (
                      <>
                        Confirm Appointment
                        <ArrowRight size={20} />
                      </>
                    )}
                  </motion.button>
                </div>

                <textarea
                  placeholder="Any special requests? (optional)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  style={{ ...styles.textarea, maxWidth: '100%', marginTop: isMobile ? '16px' : '24px' }}
                  data-testid="special-requests-textarea"
                />

                {/* Communication Consent */}
                <div style={{
                  marginTop: isMobile ? '16px' : '24px',
                  padding: '20px',
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '14px',
                  width: '100%',
                  boxSizing: 'border-box',
                }}>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: 'rgba(255,255,255,0.7)',
                    marginBottom: '14px',
                    fontFamily: "'Oswald', sans-serif",
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                  }}>
                    Communication Preferences *
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: 'rgba(255,255,255,0.4)',
                    marginBottom: '14px',
                    fontFamily: "'Montserrat', sans-serif",
                  }}>
                    Please select at least one method for appointment reminders and confirmations.
                  </div>
                  <label style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    cursor: 'pointer',
                    marginBottom: '12px',
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: '13px',
                    color: '#fff',
                  }}>
                    <input
                      type="checkbox"
                      checked={smsConsent}
                      onChange={(e) => setSmsConsent(e.target.checked)}
                      style={{ width: '18px', height: '18px', accentColor: '#dc2626', cursor: 'pointer', marginTop: '1px', flexShrink: 0 }}
                      data-testid="sms-consent-checkbox"
                    />
                    <span>I agree to receive text messages from <strong style={{ color: '#dc2626' }}>{businessSettings?.shop_name || 'this business'}</strong> for appointment reminders, confirmations, and updates. Msg & data rates may apply.</span>
                  </label>
                  <label style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    cursor: 'pointer',
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: '13px',
                    color: '#fff',
                  }}>
                    <input
                      type="checkbox"
                      checked={emailConsent}
                      onChange={(e) => setEmailConsent(e.target.checked)}
                      style={{ width: '18px', height: '18px', accentColor: '#dc2626', cursor: 'pointer', marginTop: '1px', flexShrink: 0 }}
                      data-testid="email-consent-checkbox"
                    />
                    <span>I agree to receive email notifications from <strong style={{ color: '#dc2626' }}>{businessSettings?.shop_name || 'this business'}</strong> for appointment reminders, confirmations, and updates.</span>
                  </label>
                  {!smsConsent && !emailConsent && (
                    <div style={{
                      marginTop: '10px',
                      fontSize: '11px',
                      color: '#ef4444',
                      fontFamily: "'Montserrat', sans-serif",
                    }}>
                      * At least one option must be selected to complete your booking.
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes orbFloat1 {
          0% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(80px, 60px) scale(1.1); }
          50% { transform: translate(-40px, 120px) scale(0.95); }
          75% { transform: translate(-80px, -30px) scale(1.05); }
          100% { transform: translate(0, 0) scale(1); }
        }
        @keyframes orbFloat2 {
          0% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(-60px, -80px) scale(1.08); }
          50% { transform: translate(50px, -40px) scale(0.92); }
          75% { transform: translate(70px, 60px) scale(1.12); }
          100% { transform: translate(0, 0) scale(1); }
        }
      `}</style>
    </div>
  );
}

/* ---------------- COMPONENTS ---------------- */

function InputField({ label, icon: Icon, value, onChange, placeholder, type = 'text', required, inputRef }) {
  const [focused, setFocused] = useState(false);

  return (
    <div style={styles.inputWrapper}>
      <label style={styles.label}>
        {label}
        {required && <span style={styles.required}>*</span>}
      </label>
      <div style={styles.inputContainer}>
        {Icon && <Icon size={18} style={styles.inputIcon} />}
        <input
          ref={inputRef}
          type={type}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            ...styles.input(!!Icon),
            borderColor: focused ? 'rgba(220, 38, 38, 0.5)' : 'rgba(255,255,255,0.08)',
            background: focused ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.03)',
          }}
          data-testid={`input-${label.toLowerCase().replace(/\s+/g, '-')}`}
        />
      </div>
    </div>
  );
}

function SelectField({ label, value, onChange, options, placeholder, disabled }) {
  const [focused, setFocused] = useState(false);
  
  return (
    <div style={styles.inputWrapper}>
      <label style={styles.label}>{label}</label>
      <div style={styles.inputContainer}>
        <select
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          disabled={disabled}
          style={{
            ...styles.select,
            borderColor: focused ? 'rgba(220, 38, 38, 0.5)' : 'rgba(255,255,255,0.08)',
            background: focused ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.03)',
            opacity: disabled ? 0.5 : 1,
          }}
          data-testid={`select-${label.toLowerCase()}`}
        >
          <option value="" style={{ background: '#1a1a1a' }}>{placeholder}</option>
          {options.map((opt) => (
            <option key={opt} value={opt} style={{ background: '#1a1a1a' }}>
              {opt}
            </option>
          ))}
        </select>
        <ChevronDown size={18} style={styles.selectArrow} />
      </div>
    </div>
  );
}

function VehicleTypeCard({ type, selected, onClick }) {
  const [hovered, setHovered] = useState(false);
  
  return (
    <motion.button
      whileHover={{ scale: 1.02, y: -4 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...styles.vehicleCard(selected),
        borderColor: hovered && !selected ? 'rgba(255,255,255,0.2)' : selected ? '#dc2626' : 'rgba(255,255,255,0.08)',
      }}
      data-testid={`vehicle-type-${type.id}`}
    >
      {selected && (
        <CheckCircle2 size={20} style={styles.checkIcon} />
      )}
      <div style={styles.vehicleLabel}>{type.label}</div>
      {type.upcharge > 0 && (
        <div style={styles.vehicleUpcharge}>+${type.upcharge}</div>
      )}
    </motion.button>
  );
}

function ServiceCard({ service, price, selected, onClick, disabled }) {
  const [hovered, setHovered] = useState(false);
  const Icon = service.icon;
  const mobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <motion.button
      whileHover={!disabled ? { scale: 1.02, y: -4 } : {}}
      whileTap={!disabled ? { scale: 0.98 } : {}}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...styles.serviceCard(selected),
        borderColor: hovered && !selected ? 'rgba(255,255,255,0.2)' : selected ? '#dc2626' : 'rgba(255,255,255,0.08)',
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        ...(mobile && { padding: '16px', borderRadius: '14px' }),
      }}
      data-testid={`service-${service.id}`}
      disabled={disabled}
    >
      {service.popular && (
        <div style={{ ...styles.popularBadge, ...(mobile && { top: '-8px', right: '12px', fontSize: '10px', padding: '3px 10px' }) }}>POPULAR</div>
      )}
      {selected && (
        <CheckCircle2 size={mobile ? 18 : 20} style={styles.checkIcon} />
      )}
      <div style={{ ...styles.serviceName, ...(mobile && { fontSize: '14px', marginBottom: '4px' }) }}>{service.name}</div>
      <div style={{ ...styles.serviceDesc, ...(mobile && { fontSize: '12px', marginBottom: '10px' }) }}>
        {service.description?.split('\n').filter(line => line.trim()).map((line, i) => (
          <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
            <span style={{ color: '#ef4444', flexShrink: 0 }}>•</span>
            <span>{line.trim()}</span>
          </div>
        ))}
      </div>
      {service.duration_minutes && (
        <div style={{
          fontFamily: "'Montserrat', sans-serif",
          fontSize: mobile ? '12px' : '13px',
          color: 'rgba(255,255,255,0.5)',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
        }}>
          <svg width={mobile ? 13 : 14} height={mobile ? 13 : 14} viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          Est. {service.duration_minutes >= 60
            ? (service.duration_minutes % 60 === 0
              ? `${service.duration_minutes / 60} hr${service.duration_minutes / 60 !== 1 ? 's' : ''}`
              : `${(service.duration_minutes / 60).toFixed(1)} hrs`)
            : `${service.duration_minutes} min`}
        </div>
      )}
      <div style={{ ...styles.servicePrice, ...(mobile && { fontSize: '18px' }) }}>
        {typeof price === 'number' ? `$${fmt(price)}` : price}
      </div>
    </motion.button>
  );
}

function CalendarPicker({ selected, onSelect }) {
  const [viewDate, setViewDate] = useState(new Date());
  const [closedDates, setClosedDates] = useState([]);
  const [businessHours, setBusinessHours] = useState([]);
  const [minimumNoticeDays, setMinimumNoticeDays] = useState(1);
  const [fullyBookedDates, setFullyBookedDates] = useState([]);
  const calMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Calculate minimum bookable date
  const minBookableDate = new Date(today);
  minBookableDate.setDate(minBookableDate.getDate() + minimumNoticeDays);
  minBookableDate.setHours(0, 0, 0, 0);

  const currentMonth = viewDate.toLocaleString('default', { month: 'long' });
  const currentYear = viewDate.getFullYear();
  const viewMonth = viewDate.getMonth();
  const daysInMonth = new Date(currentYear, viewMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, viewMonth, 1).getDay();

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dayNamesLower = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

  // Fetch schedule (closed dates and business hours) and business settings
  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        const response = await fetch(`${API_URL}/api/schedule`);
        if (response.ok) {
          const data = await response.json();
          setClosedDates(data.closed_dates || []);
          setBusinessHours(data.business_hours || []);
        }
      } catch (err) {
        console.error('Failed to fetch schedule:', err);
      }
    };

    const fetchBusinessSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/api/settings/business`);
        if (response.ok) {
          const data = await response.json();
          setMinimumNoticeDays(data.minimum_booking_notice_days || 1);
        }
      } catch (err) {
        console.error('Failed to fetch business settings:', err);
      }
    };

    fetchSchedule();
    fetchBusinessSettings();
  }, []);

  // Fetch fully-booked dates for the current view month
  useEffect(() => {
    const fetchFullyBooked = async () => {
      try {
        const response = await fetch(`${API_URL}/api/availability/month/${currentYear}/${viewMonth + 1}`);
        if (response.ok) {
          const data = await response.json();
          setFullyBookedDates(data.fully_booked_dates || []);
        }
      } catch (err) {
        console.error('Failed to fetch fully booked dates:', err);
      }
    };
    fetchFullyBooked();
  }, [currentYear, viewMonth]);

  const handlePrevMonth = () => {
    setViewDate(new Date(currentYear, viewMonth - 1, 1));
  };

  const handleNextMonth = () => {
    setViewDate(new Date(currentYear, viewMonth + 1, 1));
  };

  const isDateSelected = (day) => {
    if (!selected) return false;
    return selected.getDate() === day &&
           selected.getMonth() === viewMonth &&
           selected.getFullYear() === currentYear;
  };

  const isDateClosed = (day) => {
    const dateStr = `${currentYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return closedDates.some(cd => cd.date === dateStr);
  };

  const isDateFullyBooked = (day) => {
    const dateStr = `${currentYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return fullyBookedDates.includes(dateStr);
  };

  const isDayOfWeekOpen = (dayOfWeek) => {
    // dayOfWeek: 0 = Sunday, 1 = Monday, etc.
    const dayName = dayNamesLower[dayOfWeek];
    const daySchedule = businessHours.find(h => h.day === dayName);
    return daySchedule ? daySchedule.is_open : true; // Default to open if not found
  };

  const mobileCalStyles = calMobile ? {
    weekdays: { gap: '2px', marginBottom: '6px' },
    weekday: { fontSize: '10px', padding: '4px 0' },
    days: { gap: '3px' },
    day: { borderRadius: '8px', fontSize: '12px' },
  } : {};

  return (
    <div>
      <div style={styles.calendarHeader}>
        <div style={{ ...styles.calendarMonth, ...(calMobile && { fontSize: '16px' }) }}>{currentMonth} {currentYear}</div>
        <div style={styles.calendarNav}>
          <button style={{ ...styles.calendarNavBtn, ...(calMobile && { width: '32px', height: '32px' }) }} onClick={handlePrevMonth}>
            <ChevronLeft size={calMobile ? 16 : 18} />
          </button>
          <button style={{ ...styles.calendarNavBtn, ...(calMobile && { width: '32px', height: '32px' }) }} onClick={handleNextMonth}>
            <ChevronRight size={calMobile ? 16 : 18} />
          </button>
        </div>
      </div>

      <div style={{ ...styles.calendarWeekdays, ...mobileCalStyles.weekdays }}>
        {weekDays.map((day) => (
          <div key={day} style={{ ...styles.calendarWeekday, ...mobileCalStyles.weekday }}>{day}</div>
        ))}
      </div>

      <div style={{ ...styles.calendarDays, ...mobileCalStyles.days }}>
        {/* Empty cells for days before the first of the month */}
        {Array.from({ length: firstDayOfMonth }).map((_, i) => (
          <div key={`empty-${i}`} />
        ))}

        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const dateForDay = new Date(currentYear, viewMonth, day);
          dateForDay.setHours(0, 0, 0, 0);
          const dayOfWeek = dateForDay.getDay();
          const isPast = dateForDay < minBookableDate;
          const isToday = dateForDay.getTime() === today.getTime();
          const isSelected = isDateSelected(day);
          const isClosed = isDateClosed(day);
          const isDayNotOpen = !isDayOfWeekOpen(dayOfWeek);
          const isFullyBooked = isDateFullyBooked(day);
          const isDisabled = isPast || isClosed || isDayNotOpen || isFullyBooked;

          // Determine tooltip message
          let tooltipMessage = '';
          if (isClosed) {
            tooltipMessage = 'Closed';
          } else if (isDayNotOpen) {
            tooltipMessage = `Closed - ${weekDays[dayOfWeek]} not available`;
          } else if (isFullyBooked) {
            tooltipMessage = 'Fully booked';
          } else if (isPast && dateForDay >= today) {
            tooltipMessage = `Minimum ${minimumNoticeDays} day${minimumNoticeDays > 1 ? 's' : ''} notice required`;
          }

          return (
            <motion.button
              key={day}
              whileHover={!isDisabled ? { scale: 1.15 } : {}}
              whileTap={!isDisabled ? { scale: 0.95 } : {}}
              onClick={() => !isDisabled && onSelect(new Date(currentYear, viewMonth, day))}
              disabled={isDisabled}
              style={{ ...styles.calendarDay(isSelected, isToday, isPast, isClosed || isDayNotOpen || isFullyBooked), ...mobileCalStyles.day }}
              data-testid={`calendar-day-${day}`}
              title={tooltipMessage}
            >
              {day}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

function TimeSlotPicker({ slots, selected, onSelect }) {
  const timeMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div style={{
      ...styles.timeGrid,
      ...(timeMobile && { gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }),
    }}>
      {slots.map((slot) => (
        <motion.button
          key={slot.time}
          whileHover={slot.available ? { scale: 1.03 } : {}}
          whileTap={slot.available ? { scale: 0.97 } : {}}
          onClick={() => slot.available && onSelect(slot.time)}
          disabled={!slot.available}
          style={{
            ...styles.timeSlot(selected === slot.time, slot.available),
            ...(timeMobile && { padding: '12px 8px', fontSize: '13px', borderRadius: '10px' }),
          }}
          data-testid={`time-slot-${slot.time.replace(/\s+/g, '-').toLowerCase()}`}
        >
          <Clock size={timeMobile ? 12 : 14} style={{ color: 'inherit' }} />
          {slot.time}
        </motion.button>
      ))}
    </div>
  );
}
